import { useState, useEffect, useMemo } from "react";
import { WizardDebugButton } from "@/components/ReportBugDialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  FileText, Search, Building2, User, CheckCircle, 
  AlertCircle, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Download, Loader2,
  Calculator, Calendar, DollarSign, RefreshCw, Plus, MapPin,
  FileSignature, Send, X, Building, UserCheck, Filter
} from "lucide-react";
import api, { contractsDocAsWord } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { cn } from "@/lib/utils";
import OnboardingStatusDashboard from "./OnboardingStatusDashboard";
import RecentSmsTable from "./RecentSmsTable";
import TilePanel from "./TilePanel";
import DocuSignSmsPanel from "./DocuSignSmsPanel";
import CapayAccountCard from "./CapayAccountCard";

// AUS DocuPilot Template ID
const AUS_FTLA_TEMPLATE_ID = "103526";

// Default fee values
const DEFAULT_MISSED_PAYMENT_FEE = 20; // $20 per day
const DEFAULT_DEFAULT_FEE_PERCENT = 0.10; // 10% of loan amount

// SimplyFunded AUS default details
const SF_AUS_ADDRESS = "Level 12, 31 Queen Street, Melbourne, VIC 3000";
const SF_AUS_PHONE = "+61 3 1234 5678";

export default function AUSDocumentGeneration({ wizardOnly = false, wizardDeal = null, wizardCompany = "", onWizardClose = null }) {
  const { user } = useAuth();
  const { regionConfig } = useRegion();
  
  // Dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [activeTile, setActiveTile] = useState(null); // 'customer' | 'contract' | 'esign' | 'dd' | null
  const [generating, setGenerating] = useState(false);
  
  // Company search state
  const [companySearch, setCompanySearch] = useState("");
  // Deep-link prefill from the AUS Credit Approved checklist (?sfa=1234&company=X)
  // or via the wizard pop-out props (wizardDeal/wizardCompany)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sfa = ((wizardDeal || params.get("sfa")) || "").replace(/[^0-9]/g, "");
    const company = wizardCompany || params.get("company") || "";
    if (sfa) setLoanDetails(prev => ({ ...prev, fundingAgreementNumber: sfa }));
    if (company) setCompanySearch(company);
    if (wizardOnly) {
      setCurrentStep(1);
      setShowGenerateDialog(true);
      if (company) setTimeout(() => searchCompany(company), 400);
    }
    if (sfa || company) toast.info(`Prefilled from checklist: ${sfa ? `SFA-${sfa}` : ""} ${company}`.trim());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [searchRegisterErrors, setSearchRegisterErrors] = useState({});   // {asic?: msg, abr?: msg} from the last search
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [searchingCompany, setSearchingCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  
  // Lender bank details (from Airtable)
  const [lenderBankDetails, setLenderBankDetails] = useState(null);
  const [loadingLenderBank, setLoadingLenderBank] = useState(false);
  const [lenderBankError, setLenderBankError] = useState(null);
  // Settlement account printed on the FTLA (Oct 2026): capay (Cuscal house) | ebury | settlement_account
  const [capayAccount, setCapayAccount] = useState(null); // eslint-disable-line no-unused-vars
  const [settlementChoice, setSettlementChoice] = useState("capay");
  const [settlementOptions, setSettlementOptions] = useState(null);
  const chosenSettlement = settlementOptions?.options?.find((o) => o.key === settlementChoice) || null;
  
  // Form state - Core loan details
  const [loanDetails, setLoanDetails] = useState({
    fundingAgreementNumber: "",
    agreementDate: new Date().toISOString().split('T')[0],
    commencementDate: "",
    loanAmount: 0,
    factorRate: 1.5,
    termInWeeks: 26,
    paymentFrequency: "Daily",
    paymentDay: "Tuesday", // Day of week for weekly payments
    arrangementFee: 0,
    legalFee: 0,
    missedPaymentFee: DEFAULT_MISSED_PAYMENT_FEE,
  });
  
  // Borrower address (manual entry with postcode lookup)
  const [borrowerAddress, setBorrowerAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    suburb: "",
    state: "",
    postcode: ""
  });
  
  // Address search state
  const [addressSearch, setAddressSearch] = useState("");
  const [addressResults, setAddressResults] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  
  // Borrower bank details
  const [borrowerBankDetails, setBorrowerBankDetails] = useState({
    accountName: "",
    bsb: "",
    accountNumber: ""
  });
  
  // Borrower service email — clause 16.5 (service of legal process). Pre-populated from
  // the first signing director's email when available; remains editable by ops.
  const [borrowerServiceEmail, setBorrowerServiceEmail] = useState("");
  
  // Directors/Signatories (manual entry for now - ASIC EDGE API requires approval)
  const [directors, setDirectors] = useState([]);
  const [signingDirector1, setSigningDirector1] = useState(null);
  const [signingDirector2, setSigningDirector2] = useState(null);
  const [addSecondDirector, setAddSecondDirector] = useState(false);
  const [signing1Designation, setSigning1Designation] = useState("Director");
  const [signing2Designation, setSigning2Designation] = useState("Director/Secretary");
  
  // SimplyFunded (Lender) internal signer
  const [sfSigner, setSfSigner] = useState({ name: 'Dean Morley', email: 'dean.morley@simplyfunded.co.uk', title: 'Managing Director' });
  // Who signs on behalf of the Lender: "dean_morley" (default) | "me" (portal user) | "other" (manual)
  // Anything other than dean_morley adds "Authorised Signatory under Power Of Attorney"
  // beneath the title on the FTLA + GSD templates (POA active May 2026).
  const [lenderSignerType, setLenderSignerType] = useState("dean_morley");
  
  // Default SF address for witness
  const SF_DEFAULT_ADDRESS = "Level 2, 11 York Street, Sydney NSW 2000";
  
  // Sumsub director state
  const [recentDirectors, setRecentDirectors] = useState([]);
  const [loadingDirectors, setLoadingDirectors] = useState(false);
  const [directorSearch, setDirectorSearch] = useState("");
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [sumsubStatus, setSumsubStatus] = useState(null);
  const [showAddDirectorDialog, setShowAddDirectorDialog] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [sumsubExternalId, setSumsubExternalId] = useState("");
  const [newDirectorForm, setNewDirectorForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: ""
  });
  
  // Manual business entry state (for when ASIC/ABR doesn't return results)
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualBusinessEntry, setManualBusinessEntry] = useState({
    company_name: "",
    abn: "",
    acn: "",
    entity_type: "Sole Trader",
    status: "Active",
    registered_address: ""
  });
  
  // Guarantors (up to 3) - with guarantee type (personal or company)
  const [guarantors, setGuarantors] = useState([
    { 
      guaranteeType: "personal", // "personal" or "company"
      // Personal guarantee fields
      personalName: "",
      personalAddress: "",
      personalEmail: "",
      // Company guarantee fields  
      companyName: "", 
      companyAddress: "", 
      signatoryName: "", // Person signing on behalf of company
      signatoryEmail: "",
      // Email for service of legal process (Gladwin Legal clause 16.5).
      // Pre-populated from personalEmail / signatoryEmail at payload-build time, editable.
      serviceEmail: "",
      // Common
      useDirectorInfo: false 
    }
  ]);
  
  // Generated document and e-signing
  const [generatedDoc, setGeneratedDoc] = useState(null);
  // Sales ask (Sep 2026): Word download + replace-before-DocuSign
  const [replacedDoc, setReplacedDoc] = useState(null);
  const [wordBusy, setWordBusy] = useState(false);
  const downloadAsWord = async () => {
    setWordBusy(true);
    try {
      const blob = await contractsDocAsWord(generatedDoc.file_url, `FTLA - ${companyDetails?.company_name || "document"}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `FTLA - ${companyDetails?.company_name || "document"}.docx`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Word version downloaded — edit it, then use Replace to send your version to DocuSign");
    } catch (e) {
      let msg = e?.message;
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* ignore */ }
      toast.error("Word conversion failed: " + msg);
    } finally { setWordBusy(false); }
  };
  const handleReplaceDoc = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File too large (15 MB max)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const isDocx = f.name.toLowerCase().endsWith(".docx");
      setReplacedDoc({ file_base64: String(reader.result).split(",")[1], filename: f.name,
        content_type: isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf" });
      toast.success(`${f.name} will replace the generated FTLA in the DocuSign envelope`);
    };
    reader.readAsDataURL(f);
  };
  const [gsdDoc, setGsdDoc] = useState(null); // General Security Deed document
  const [eSigningDocs, setESigningDocs] = useState({
    ftla: true, // Fixed Term Loan Agreement - always included
    gsd: false, // General Security Deed - optional
    bpd: false, // NCC s13 Business Purpose Declaration - forced on for sole traders, optional otherwise
    sumsubReport: false // Sumsub verification report - optional
  });
  // Sole trader = ABR entity type "Individual/Sole Trader" (or manual entry) → declaration mandatory
  const bpdRequired = !!companyDetails?.is_sole_trader;
  const bpdIncluded = bpdRequired || !!eSigningDocs.bpd;
  const [signers, setSigners] = useState([
    { name: "", email: "", role: "Borrower Director" }
  ]);
  const [sendingForSigning, setSendingForSigning] = useState(false);
  const [showDocupilotIframe, setShowDocupilotIframe] = useState(false);
  const [envelopeId, setEnvelopeId] = useState("");
  
  // DocuPilot workspace ID (from your account)
  const DOCUPILOT_WORKSPACE_ID = "b743a0fb";
  const DOCUPILOT_SUBDOMAIN = "simplyfunded";

  // DocuSign state (for e-signing after DocuPilot generates the doc)
  const [sendingToDocuSign, setSendingToDocuSign] = useState(false);
  const [docuSignResult, setDocuSignResult] = useState(null);
  const [showDocuSignIframe, setShowDocuSignIframe] = useState(false);
  const [docuSignIframeUrl, setDocuSignIframeUrl] = useState(null);

  // DocuSign Envelopes List state
  const [envelopes, setEnvelopes] = useState([]);
  const [loadingEnvelopes, setLoadingEnvelopes] = useState(false);
  const [envelopesError, setEnvelopesError] = useState(null);
  const [envelopesExpanded, setEnvelopesExpanded] = useState(true);
  const [envelopeFilter, setEnvelopeFilter] = useState("open"); // "open", "completed", "all"
  const [expandedEnvelopes, setExpandedEnvelopes] = useState({}); // Track which envelope items are expanded
  const [resumingEnvelopeId, setResumingEnvelopeId] = useState(null); // envelope_id currently being resumed

  // Toggle individual envelope expansion
  const toggleEnvelopeExpand = (envelopeId) => {
    setExpandedEnvelopes(prev => ({
      ...prev,
      [envelopeId]: !prev[envelopeId]
    }));
  };

  // GoCardless Mandate state (Step 6)
  const [gcMandate, setGcMandate] = useState({
    scheme: "au_pay_to", // default to PayTo — ~80% of AU banks support it. BECS is the fallback.
    productName: "",
    paymentReference: "",
    frequency: "weekly",
    amount: "",
    firstPaymentDate: "",
    // When true (default for PayTo), keep `amount` locked to the loan's
    // calculated per-payment amount so the PayTo cap exactly matches the
    // loan schedule. Operator can untick to override.
    autoCapFromLoan: true,
  });
  const [gcCreating, setGcCreating] = useState(false);
  const [gcResult, setGcResult] = useState(null);
  const [gcLinkCopied, setGcLinkCopied] = useState(false);
  const [gcEmailTo, setGcEmailTo] = useState("");
  const [gcSendingEmail, setGcSendingEmail] = useState(false);
  const [gcEmailSent, setGcEmailSent] = useState(false);

  // SMS state (Aircall)
  const [gcSmsTo, setGcSmsTo] = useState("");
  const [gcAircallNumbers, setGcAircallNumbers] = useState([]);
  const [gcLoadingNumbers, setGcLoadingNumbers] = useState(false);
  const [gcAircallPhoneId, setGcAircallPhoneId] = useState("");
  const [gcSendingSms, setGcSendingSms] = useState(false);
  const [gcSmsResult, setGcSmsResult] = useState(null);
  const [gcSmsStatus, setGcSmsStatus] = useState(null); // {resolved_status, received_at, message_id}
  const [gcSmsTemplate, setGcSmsTemplate] = useState(""); // master template (with placeholders)
  const [gcSmsBody, setGcSmsBody] = useState(""); // editable body (placeholders pre-substituted)

  // Load lender bank details on mount
  useEffect(() => {
    loadLenderBankDetails();
    checkSumsubStatus();
    loadDocuSignEnvelopes();  // Load envelopes on mount
    loadRecentDirectors();
  }, []);

  const loadLenderBankDetails = async () => {
    try {
      setLoadingLenderBank(true);
      setLenderBankError(null);
      const response = await api.get('/aus/bank-details/lender');
      if (response.data.success) {
        setLenderBankDetails(response.data.bank_details);
      } else {
        setLenderBankDetails(null);
        setLenderBankError(
          response.data.message ||
            "No lender bank account is marked as currently in use."
        );
      }
    } catch (error) {
      // Deliberately no fallback. This account is printed onto the loan
      // agreement, so substituting a remembered one is worse than showing
      // nothing: it is the value a borrower pays into, and the record it
      // mirrors is expected to change. The backend refuses rather than
      // guessing, and that refusal has to reach the operator instead of being
      // replaced here.
      setLenderBankDetails(null);
      setLenderBankError(
        error?.response?.data?.detail ||
          "Could not load the lender bank account, so document generation is blocked."
      );
    } finally {
      setLoadingLenderBank(false);
    }
  };

  // Check if Sumsub integration is configured
  const checkSumsubStatus = async () => {
    try {
      const response = await api.get('/aus/sumsub/status');
      setSumsubStatus(response.data);
    } catch (error) {
      console.error("Error checking Sumsub status:", error);
      setSumsubStatus({ configured: false });
    }
  };

  // Load DocuSign envelopes for the AUS region
  const loadDocuSignEnvelopes = async () => {
    try {
      setLoadingEnvelopes(true);
      setEnvelopesError(null);
      const response = await api.get('/contracts/envelopes/list', {
        params: { region: 'aus', days: 30 }
      });
      
      if (response.data.success) {
        setEnvelopes(response.data.envelopes || []);
      }
    } catch (error) {
      console.error("Error loading DocuSign envelopes:", error);
      setEnvelopesError(error.response?.data?.detail || "Failed to load envelopes");
      setEnvelopes([]);
    } finally {
      setLoadingEnvelopes(false);
    }
  };

  // Resume a draft DocuSign envelope by fetching a fresh sender view URL and
  // re-opening the iframe modal. Only valid for envelopes with status === 'created'.
  const handleResumeEnvelope = async (envelopeId) => {
    if (!envelopeId) return;
    try {
      setResumingEnvelopeId(envelopeId);
      const response = await api.get(`/contracts/envelope/${envelopeId}/resume`);
      const senderUrl = response.data?.sender_view_url;
      if (senderUrl) {
        setDocuSignResult({ envelope_id: envelopeId, document_count: 1, signer_count: 1 });
        setDocuSignIframeUrl(senderUrl);
        setShowDocuSignIframe(true);
      } else {
        toast.error(response.data?.message || "Could not reopen envelope — it may already be sent");
      }
    } catch (error) {
      console.error("Error resuming envelope:", error);
      toast.error(error.response?.data?.detail || "Failed to reopen envelope");
    } finally {
      setResumingEnvelopeId(null);
    }
  };

  // Load recent directors from local database
  const loadRecentDirectors = async (search = "") => {
    try {
      setLoadingDirectors(true);
      const params = new URLSearchParams({ limit: "10" });
      if (search) params.append("search", search);
      
      const response = await api.get(`/aus/directors/recent?${params.toString()}`);
      
      if (response.data.success) {
        setRecentDirectors(response.data.directors || []);
      }
    } catch (error) {
      console.error("Error loading recent directors:", error);
      setRecentDirectors([]);
    } finally {
      setLoadingDirectors(false);
    }
  };

  // Search directors (with debounce effect from input)
  const searchDirectors = async (query) => {
    if (query.length >= 2) {
      await loadRecentDirectors(query);
    } else if (query.length === 0) {
      await loadRecentDirectors();
    }
  };

  // Sync a new director from Sumsub
  const syncDirectorFromSumsub = async (externalUserId) => {
    try {
      setLoadingDirectors(true);
      const response = await api.post(`/aus/directors/sync?external_user_id=${encodeURIComponent(externalUserId)}`);
      
      if (response.data.success) {
        toast.success(`Director "${response.data.director.full_name}" synced from Sumsub`);
        // Refresh the list
        await loadRecentDirectors();
        // Select the newly synced director
        selectDirector(response.data.director);
        // Close dialog
        setShowSyncDialog(false);
        setSumsubExternalId("");
      } else {
        toast.error(response.data.message || "Failed to sync director from Sumsub");
      }
    } catch (error) {
      console.error("Sync director error:", error);
      toast.error("Failed to sync director from Sumsub");
    } finally {
      setLoadingDirectors(false);
    }
  };

  // Delete a director from local storage
  const deleteDirector = async (directorId) => {
    try {
      const response = await api.delete(`/aus/directors/${directorId}`);
      if (response.data.success) {
        toast.success("Director removed");
        // Refresh list
        await loadRecentDirectors();
        // Clear selection if deleted director was selected
        if (selectedDirector?.sumsub_applicant_id === directorId) {
          setSelectedDirector(null);
        }
      } else {
        toast.error("Failed to remove director");
      }
    } catch (error) {
      console.error("Delete director error:", error);
      toast.error("Failed to remove director");
    }
  };

  // Add director manually
  const addDirectorManually = async () => {
    if (!newDirectorForm.full_name) {
      toast.error("Please enter the director's full name");
      return;
    }
    
    try {
      const response = await api.post('/aus/directors/add', newDirectorForm);
      
      if (response.data.success) {
        toast.success(`Director "${newDirectorForm.full_name}" added successfully`);
        setShowAddDirectorDialog(false);
        setNewDirectorForm({ full_name: "", email: "", phone: "", address: "" });
        // Refresh and select
        await loadRecentDirectors();
        selectDirector(response.data.director);
      } else {
        toast.error(response.data.error || "Failed to add director");
      }
    } catch (error) {
      console.error("Add director error:", error);
      toast.error("Failed to add director");
    }
  };

  // Select a director and apply their info
  const selectDirector = (director) => {
    setSelectedDirector(director);
    
    // Auto-fill signing director 1 with full director object
    if (director.full_name) {
      setSigningDirector1(director);
    }
    
    toast.success(`Selected: ${director.full_name}`);
  };

  // Apply selected director data to a specific field
  const applyDirectorToField = (field, directorNum = 1) => {
    if (!selectedDirector) {
      toast.error("Please select a director first");
      return;
    }
    
    if (field === "director") {
      if (directorNum === 1) {
        setSigningDirector1(selectedDirector);
        
        // Also pre-fill guarantor signatory if it's a company guarantee and signatoryName is empty
        if (guarantors[0]?.guaranteeType === "company" && !guarantors[0]?.signatoryName) {
          const updated = [...guarantors];
          updated[0] = {
            ...updated[0],
            signatoryName: selectedDirector.full_name,
            signatoryEmail: selectedDirector.email || ""
          };
          setGuarantors(updated);
          toast.info("Also pre-filled guarantor signatory name");
        }
      } else {
        setSigningDirector2(selectedDirector);
      }
    }
    
    toast.success(`Applied ${selectedDirector.full_name} as Director ${directorNum}`);
  };

  // Copy director info to guarantor
  const copyDirectorToGuarantor = (guarantorIndex) => {
    if (!selectedDirector) {
      toast.error("Please select a director first");
      return;
    }
    
    const updated = [...guarantors];
    const guaranteeType = updated[guarantorIndex].guaranteeType;
    
    if (guaranteeType === "personal") {
      // For personal guarantee, fill personal fields
      updated[guarantorIndex] = {
        ...updated[guarantorIndex],
        personalName: selectedDirector.full_name,
        personalAddress: selectedDirector.address || updated[guarantorIndex].personalAddress,
        personalEmail: selectedDirector.email || updated[guarantorIndex].personalEmail || "",
        useDirectorInfo: true
      };
    } else {
      // For company guarantee, fill the signatory fields
      updated[guarantorIndex] = {
        ...updated[guarantorIndex],
        signatoryName: selectedDirector.full_name,
        signatoryEmail: selectedDirector.email || updated[guarantorIndex].signatoryEmail || "",
        useDirectorInfo: true
      };
    }
    setGuarantors(updated);
    
    // Show appropriate message
    if (selectedDirector.email) {
      toast.success(`Copied director info to Guarantor ${guarantorIndex + 1}`);
    } else {
      toast.success(`Copied director info to Guarantor ${guarantorIndex + 1} (email not in Sumsub - please enter manually)`);
    }
  };

  // Auto-calculated fields
  const calculatedFields = useMemo(() => {
    const { loanAmount, factorRate, termInWeeks, paymentFrequency, paymentDay, arrangementFee, legalFee, commencementDate } = loanDetails;
    
    // Factor Amount = (Factor Rate - 1) × Loan Amount
    const factorAmount = (factorRate - 1) * loanAmount;
    
    // Upfront Fees = Arrangement Fee + Legal Fee
    const upfrontFees = arrangementFee + legalFee;
    
    // Total Loan Amount = Loan Amount + Factor Amount + Upfront Fees
    const totalLoanAmount = loanAmount + factorAmount + upfrontFees;
    
    // Number of payments based on frequency
    let numPayments = termInWeeks; // Weekly
    let paymentDays = "";
    
    if (paymentFrequency === "Daily") {
      numPayments = termInWeeks * 5; // 5 business days per week
      paymentDays = "Monday, Tuesday, Wednesday, Thursday, Friday";
    } else {
      // Use selected payment day for weekly
      paymentDays = paymentDay || "Tuesday";
    }
    
    // Payment Amount = Total Loan Amount / Number of Payments
    const paymentAmount = numPayments > 0 ? totalLoanAmount / numPayments : 0;
    
    // Default Fee = 10% of loan amount
    const defaultFee = loanAmount * DEFAULT_DEFAULT_FEE_PERCENT;
    
    // Maturity Date = Commencement Date + Term in Weeks
    let maturityDate = "";
    if (commencementDate) {
      const startDate = new Date(commencementDate);
      startDate.setDate(startDate.getDate() + (termInWeeks * 7));
      maturityDate = startDate.toISOString().split('T')[0];
    }
    
    return {
      factorAmount,
      upfrontFees,
      totalLoanAmount,
      paymentAmount,
      defaultFee,
      maturityDate,
      paymentDays,
      numPayments
    };
  }, [loanDetails]);

  // Unified business search (ASIC for companies + ABR for sole traders)
  const searchBusiness = async (query) => {
    if (!query || query.length < 2) return;
    
    try {
      setSearchingCompany(true);
      // Use unified search endpoint that queries both ASIC and ABR
      const response = await api.get(`/aus/business/search?q=${encodeURIComponent(query)}&include_sole_traders=true`);
      setCompanySearchResults(response.data.businesses || []);
      setSearchRegisterErrors(response.data.errors || {});
      setSearchedOnce(true);
      if (response.data.errors?.abr) toast.error(`ABR (sole traders) search failed: ${response.data.errors.abr}`);
    } catch (error) {
      console.error("Business search error:", error);
      toast.error(error?.response?.data?.detail || "Failed to search business database");
      setCompanySearchResults([]);
      setSearchRegisterErrors({ portal: error?.response?.data?.detail || error?.message || "request failed" });
      setSearchedOnce(true);
    } finally {
      setSearchingCompany(false);
    }
  };

  // Legacy ASIC-only search (kept for backwards compatibility)
  const searchCompany = async (query) => {
    return searchBusiness(query);
  };

  // Select a business from search results
  const selectBusiness = async (business) => {
    try {
      console.log("Selected business:", business);
      
      // Use the business data directly from search results
      // No need to do another API call which might return wrong results
      const businessDetails = {
        // Sole traders contract as "LEGAL NAME T/A BUSINESS NAME"; companies as their registered name
        company_name: (business.is_sole_trader && business.display_name) || business.business_name || business.company_name,
        legal_name: business.legal_name || "",
        trading_name: business.trading_name || "",
        acn: business.acn || "",
        abn: business.abn || "",
        status: business.status,
        company_type: business.entity_type || business.company_type || (business.is_sole_trader ? "Individual/Sole Trader" : "Company"),
        registered_address: business.state && business.postcode ? `${business.state} ${business.postcode}` : "",
        state: business.state || "",
        postcode: business.postcode || "",
        is_sole_trader: business.is_sole_trader || false,
        source: business.source || "ASIC"
      };
      
      setSelectedCompany(businessDetails);
      setCompanyDetails(businessDetails);
      
      // Pre-fill borrower details
      setBorrowerBankDetails(prev => ({
        ...prev,
        accountName: businessDetails.company_name || ""
      }));
      
      // Pre-fill first guarantor with new structure
      // For company guarantees, the signatory name needs to be filled by user or from director selection
      setGuarantors([{
        guaranteeType: businessDetails.is_sole_trader ? "personal" : "company",
        personalName: businessDetails.is_sole_trader ? (businessDetails.legal_name || businessDetails.company_name) : "",
        personalAddress: businessDetails.is_sole_trader ? businessDetails.registered_address : "",
        personalEmail: "",
        companyName: !businessDetails.is_sole_trader ? businessDetails.company_name : "",
        companyAddress: !businessDetails.is_sole_trader ? businessDetails.registered_address : "",
        signatoryName: "", // Will be filled from director selection or manually
        signatoryEmail: "",
        serviceEmail: "",
        useDirectorInfo: false
      }]);
      
      // Show a toast reminder for company guarantees
      if (!businessDetails.is_sole_trader) {
        toast.info("Remember to select a director in Step 3 and use 'Use Director Info' in Step 4 to fill guarantor signatory details");
      }
      
    } catch (error) {
      console.error("Error selecting business:", error);
      toast.error("Failed to select business");
    }
  };

  // Apply manual business entry (for self-employed/sole traders not in ASIC)
  const applyManualBusinessEntry = () => {
    if (!manualBusinessEntry.company_name) {
      toast.error("Please enter the business/trading name");
      return;
    }
    
    const isSoleTrader = manualBusinessEntry.entity_type === "Sole Trader" || 
                         manualBusinessEntry.entity_type === "Individual";
    
    const businessDetails = {
      company_name: manualBusinessEntry.company_name,
      acn: manualBusinessEntry.acn || "",
      abn: manualBusinessEntry.abn || "",
      status: manualBusinessEntry.status || "Active",
      company_type: manualBusinessEntry.entity_type || "Sole Trader",
      registered_address: manualBusinessEntry.registered_address || "",
      is_sole_trader: isSoleTrader,
      source: "Manual Entry"
    };
    
    setSelectedCompany(businessDetails);
    setCompanyDetails(businessDetails);
    setShowManualEntry(false);
    
    // Pre-fill borrower details
    setBorrowerBankDetails(prev => ({
      ...prev,
      accountName: businessDetails.company_name || ""
    }));
    
    // Pre-fill guarantor
    setGuarantors([{
      guaranteeType: isSoleTrader ? "personal" : "company",
      personalName: isSoleTrader ? businessDetails.company_name : "",
      personalAddress: isSoleTrader ? businessDetails.registered_address : "",
      personalEmail: "",
      companyName: !isSoleTrader ? businessDetails.company_name : "",
      companyAddress: !isSoleTrader ? businessDetails.registered_address : "",
      signatoryName: "",
      signatoryEmail: "",
      serviceEmail: "",
      useDirectorInfo: false
    }]);
    
    toast.success("Business details entered manually");
  };

  // Start document generation
  const startGeneration = () => {
    setCurrentStep(1);
    setShowGenerateDialog(true);
    setGeneratedDoc(null);
    
    // Reset form
    setLoanDetails({
      fundingAgreementNumber: "",
      agreementDate: new Date().toISOString().split('T')[0],
      commencementDate: "",
      loanAmount: 0,
      factorRate: 1.5,
      termInWeeks: 26,
      paymentFrequency: "Daily",
      paymentDay: "Tuesday",
      arrangementFee: 0,
      legalFee: 0,
      missedPaymentFee: DEFAULT_MISSED_PAYMENT_FEE,
    });
    setBorrowerBankDetails({ accountName: "", bsb: "", accountNumber: "" });
    setBorrowerAddress({ addressLine1: "", addressLine2: "", suburb: "", state: "", postcode: "" });
    setBorrowerServiceEmail("");
    setSelectedCompany(null);
    setCompanyDetails(null);
    setCompanySearch("");
    setCompanySearchResults([]);
    setDirectors([]);
    setSigningDirector1(null);
    setSigningDirector2(null);
    setAddSecondDirector(false);
    setGuarantors([{ 
      guaranteeType: "personal",
      personalName: "",
      personalAddress: "",
      personalEmail: "",
      companyName: "", 
      companyAddress: "", 
      signatoryName: "",
      signatoryEmail: "",
      serviceEmail: "",
      useDirectorInfo: false 
    }]);
    // Reset director state
    setSelectedDirector(null);
    setDirectorSearch("");
    // Reset manual entry state
    setShowManualEntry(false);
    setManualBusinessEntry({
      company_name: "",
      abn: "",
      acn: "",
      entity_type: "Sole Trader",
      status: "Active",
      registered_address: ""
    });
    // Refresh recent directors
    loadRecentDirectors();
  };

  // Add guarantor
  const addGuarantor = () => {
    if (guarantors.length < 3) {
      setGuarantors([...guarantors, { 
        guaranteeType: "personal",
        personalName: "",
        personalAddress: "",
        personalEmail: "",
        companyName: "", 
        companyAddress: "", 
        signatoryName: "",
        signatoryEmail: "",
        serviceEmail: "",
        useDirectorInfo: false 
      }]);
    }
  };

  // Remove guarantor
  const removeGuarantor = (index) => {
    if (guarantors.length > 1) {
      setGuarantors(guarantors.filter((_, i) => i !== index));
    }
  };

  // Update guarantor
  const updateGuarantor = (index, field, value) => {
    const updated = [...guarantors];
    updated[index] = { ...updated[index], [field]: value };
    setGuarantors(updated);
  };

  // Format currency for display (with $ sign)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format currency for DocuPilot (WITHOUT $ sign - template already has it)
  const formatCurrencyForDoc = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Generate document
  const generateDocument = async () => {
    try {
      setGenerating(true);

      // === Lender signatory validation (Jul 23 2026 — Nick bug fix) ===
      // If the user picked "Me" or "Someone else" but left name/title
      // blank, refuse. Docupilot would silently render blank cells on the
      // signature block otherwise (which is what Nick had to work around
      // by placing DocuSign text fields manually).
      if (lenderSignerType !== "dean_morley") {
        const missing = [];
        if (!sfSigner.name?.trim())  missing.push("name");
        if (!sfSigner.title?.trim()) missing.push("title");
        if (missing.length) {
          toast.error(`Please fill the Lender signatory ${missing.join(" and ")} before generating (Step 4)`);
          setGenerating(false);
          return;
        }
      }

      // The lender account is the one a borrower repays into, and it is merged
      // straight onto the agreement. Absent, the two fields below would go as
      // empty strings and produce an executed document with no account on it —
      // no better than the wrong account this refusal replaced.
      // The account printed is the chosen settlement account, falling back to the
      // house account — so the refusal is on whichever of the two will be merged.
      const printedBsb = chosenSettlement?.bsb || lenderBankDetails?.bsb;
      const printedAccountNumber = chosenSettlement?.account_number || lenderBankDetails?.accountNumber;
      if (!printedBsb || !printedAccountNumber) {
        toast.error(
          lenderBankError ||
            "The lender bank account has not loaded, so the agreement cannot be generated."
        );
        setGenerating(false);
        return;
      }

      // Determine if this is a sole trader or company
      const isSoleTrader = companyDetails?.is_sole_trader || false;
      
      // Clean the funding agreement number - remove any SFA- prefix (case insensitive, with or without dash)
      // Handles: "SFA-1039", "sfa-1039", "SFA1039", "sfa1039", "SFA-SFA-1039"
      let cleanAgreementNumber = loanDetails.fundingAgreementNumber
        .replace(/^(sfa-?)+/i, '')  // Remove one or more SFA- or SFA prefixes
        .trim();
      
      // Helper function to get guarantor data
      // IMPORTANT: "GuarantorCompanyName" in the template is the PRIMARY NAME field
      // - For personal guarantee: use person's name (e.g., "Dean Morley")
      // - For company guarantee: use company name (e.g., "ABC Pty Ltd")
      // "GuarantorName" is used for the signatory name in the signing section
      const getGuarantorData = (g) => {
        if (!g) return { companyName: "", address: "", signatoryName: "" };
        
        if (g.guaranteeType === "personal") {
          return {
            // For personal guarantee, the "company name" field shows the person's name
            companyName: g.personalName || "",
            address: g.personalAddress || "",
            signatoryName: g.personalName || ""  // Same person signs
          };
        } else {
          return {
            // For company guarantee, show company name, signatory is different
            companyName: g.companyName || "",
            address: g.companyAddress || "",
            signatoryName: g.signatoryName || ""
          };
        }
      };
      
      const g1Data = getGuarantorData(guarantors[0]);
      const g2Data = getGuarantorData(guarantors[1]);
      const g3Data = getGuarantorData(guarantors[2]);
      
      // Helper: resolve the email-for-service for a guarantor, defaulting to the existing
      // personal/signatory email when ops hasn't overridden it via the dedicated input.
      const getServiceEmail = (g) => {
        if (!g) return "";
        if (g.serviceEmail && g.serviceEmail.trim()) return g.serviceEmail.trim();
        if (g.guaranteeType === "personal") return g.personalEmail || "";
        return g.signatoryEmail || "";
      };
      // Borrower service email — defaults to first signing director's email when ops left it blank
      const resolvedBorrowerServiceEmail =
        (borrowerServiceEmail && borrowerServiceEmail.trim()) ||
        (typeof signingDirector1 === 'object' ? (signingDirector1?.email || "") : "") ||
        "";
      
      // Build the data payload for DocuPilot
      const payload = {
        template_id: AUS_FTLA_TEMPLATE_ID,
        data: {
          // Agreement details - DON'T add SFA- prefix here, it's already in the DocuPilot template
          FundingAgreementNumber: cleanAgreementNumber,
          AgreementDate: formatDate(loanDetails.agreementDate),
          
          // Borrower details - handle both companies (ACN) and sole traders (ABN)
          BorrowerCompanyName: companyDetails?.company_name || "",
          BorrowerAddress: companyDetails?.registered_address || "",
          BorrowersName: companyDetails?.company_name || "",
          // Use ACN for companies, ABN for sole traders
          BorrowersACN: companyDetails?.acn || companyDetails?.abn || "",
          BorrowersABN: companyDetails?.abn || "",
          IsSoleTrader: isSoleTrader,
          
          // Loan details - use formatCurrencyForDoc (without $ sign - template has it)
          CommencementDate: formatDate(loanDetails.commencementDate),
          LoanAmount: formatCurrencyForDoc(loanDetails.loanAmount),
          FactorRate: loanDetails.factorRate.toFixed(2),
          FactorAmount: formatCurrencyForDoc(calculatedFields.factorAmount),
          UpfrontFees: formatCurrencyForDoc(calculatedFields.upfrontFees),
          TotalLoanAmount: formatCurrencyForDoc(calculatedFields.totalLoanAmount),
          PaymentAmount: formatCurrencyForDoc(calculatedFields.paymentAmount),
          TermInWeeks: loanDetails.termInWeeks.toString(),
          MaturityDate: formatDate(calculatedFields.maturityDate),
          
          // Payment details - use selected day for weekly
          PaymentFrequency: loanDetails.paymentFrequency,
          DailyPaymentsDays: loanDetails.paymentFrequency === "Daily" ? calculatedFields.paymentDays : "",
          WeeklyPaymentsDays: loanDetails.paymentFrequency === "Weekly" ? loanDetails.paymentDay : "",
          DescriptionOfPaymentCommencementDate: formatDate(loanDetails.commencementDate),
          
          // Fees - without $ sign
          ArrangementFee: formatCurrencyForDoc(loanDetails.arrangementFee),
          LegalFee: formatCurrencyForDoc(loanDetails.legalFee),
          MissedPaymentFee: formatCurrencyForDoc(loanDetails.missedPaymentFee),
          MissedPaymentFeeFrequency: "per day",
          DefaultFee: formatCurrencyForDoc(calculatedFields.defaultFee),
          
          // Borrower bank details
          BorrowerAccountName: borrowerBankDetails.accountName,
          BorrowerBranchCode: borrowerBankDetails.bsb,
          AccountNumber: borrowerBankDetails.accountNumber,
          
          // Borrower address (formatted)
          BorrowerAddress: getFormattedAddress() || companyDetails?.registered_address || "",
          
          // Lender repayment account — the chosen settlement option (server re-asserts from the same source)
          LenderAccountName: chosenSettlement?.account_name || lenderBankDetails?.accountName || "",
          LenderBranchCode: printedBsb,
          LenderBankAccountNumber: printedAccountNumber,
          
          // Signing directors - extract name strings from director objects
          Signing1Desigation: signing1Designation,
          Director2Name: typeof signingDirector1 === 'object' ? (signingDirector1?.name || signingDirector1?.full_name || "") : (signingDirector1 || ""),
          AddSecondDirector: addSecondDirector,
          Signing2Desigation: addSecondDirector ? signing2Designation : "",
          SecretaryName: addSecondDirector ? (typeof signingDirector2 === 'object' ? (signingDirector2?.name || signingDirector2?.full_name || "") : (signingDirector2 || "")) : "",
          
          // Guarantor 1 - GuarantorCompanyName shows either person name or company name
          GuarantorCompanyName: g1Data.companyName,
          GuarantorAddress: g1Data.address,
          GuarantorName: g1Data.signatoryName,
          
          // Guarantor 2
          AddSecondGuarantor: guarantors.length >= 2 && (g2Data.companyName || g2Data.signatoryName),
          GuarantorCompanyName2: g2Data.companyName,
          GuarantorAddress2: g2Data.address,
          GuarantorName2: g2Data.signatoryName,
          
          // Guarantor 3
          AddThirdGuarantor: guarantors.length >= 3 && (g3Data.companyName || g3Data.signatoryName),
          GuarantorCompanyName3: g3Data.companyName,
          GuarantorAddress3: g3Data.address,
          GuarantorName3: g3Data.signatoryName,
          
          // ---- Service of legal process emails (Gladwin Legal clause 16.5) ----
          ServiceEmailBorrower:   resolvedBorrowerServiceEmail,
          ServiceEmailGuarantor1: getServiceEmail(guarantors[0]),
          ServiceEmailGuarantor2: getServiceEmail(guarantors[1]),
          ServiceEmailGuarantor3: getServiceEmail(guarantors[2]),

          // ---- Lender signatory block (Dean Morley by default; anyone else
          // gets an "Authorised Signatory under Power Of Attorney" line
          // under the title). Replaces the old hardcoded Dean/MD text on the
          // FTLA template signature page. ----
          LenderSignerName: sfSigner.name || "Dean Morley",
          LenderSignerTitle: sfSigner.title || "Managing Director",
          LenderSignerAuthority: lenderSignerType === "dean_morley"
            ? ""
            : "Authorised Signatory under Power Of Attorney",
        },
        sf_number: cleanAgreementNumber ? `SFA-${cleanAgreementNumber}` : "",
        settlement_choice: settlementChoice,
      };
      
      // The payload is deliberately not logged. It carries the borrower's
      // account name, BSB and account number alongside the lender's, and
      // `lib/posthog.js` records console output into the session replay, so a
      // line here ships bank details to a third party.

      const response = await api.post('/aus/contracts/generate', payload);
      
      if (response.data.success) {
        setGeneratedDoc(response.data.document);
        const used = settlementOptions?.options?.find((o) => o.key === response.data.settlement_used);
        toast.success(used ? `Document generated — repayment account: ${used.label} · BSB ${used.bsb} · ${used.account_number}` : "Document generated successfully!");
        setCurrentStep(5); // Go to results step
      } else {
        toast.error("Document generation failed: " + (response.data.error || "Unknown error"));
      }
      
    } catch (error) {
      console.error("Document generation error:", error);
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.code === "capay_va_required") {
        toast.error(detail.message, { duration: 9000 });
        setCurrentStep(3);
      } else {
        toast.error("Failed to generate document");
      }
    } finally {
      setGenerating(false);
    }
  };

  // Send generated document to DocuSign for e-signature
  const sendToDocuSign = async () => {
    if (!generatedDoc?.file_url) {
      toast.error("Please generate a document first");
      return;
    }

    // Validate signers
    const validSigners = [];
    
    // Helper to get name from director (handles both object and string)
    const getDirectorName = (director) => {
      if (typeof director === 'object') {
        return director.name || director.full_name || "";
      }
      return director || "";
    };
    
    // Helper to get email from director
    const getDirectorEmail = (director) => {
      if (typeof director === 'object') {
        return director.email || "";
      }
      return "";
    };
    
    // Add signing directors
    // Anchor mapping (Jul 23 2026 — FTLA + GSD templates reuploaded):
    //   FTLA: /S1 (Lender), /S2 (Dir 1), /S3 (Dir 2), /S6..S8 (Guarantors)
    //   GSD:  /S1GSD (Lender), /S2GSD (sole D+S), /S3GSD (Dir 1 of 2),
    //         /S4GSD (Dir 2 of 2 / Secretary). Guarantors don't sign GSD.
    // Each signer gets tabs on BOTH docs in the same envelope so we send
    // an array of anchors.
    const soleDirectorMode = !addSecondDirector;
    if (signingDirector1) {
      const name = getDirectorName(signingDirector1);
      const email = getDirectorEmail(signingDirector1);
      if (name && email) {
        validSigners.push({
          name: name,
          email: email,
          role: signing1Designation,
          // The borrower (sole trader) also signs the Business Purpose Declaration (/S2BPD)
          anchorStrings: [...(soleDirectorMode ? ["/S2", "/S2GSD"] : ["/S2", "/S3GSD"]), ...(bpdIncluded ? ["/S2BPD"] : [])],
        });
      }
    }
    
    if (addSecondDirector && signingDirector2) {
      const name = getDirectorName(signingDirector2);
      const email = getDirectorEmail(signingDirector2);
      if (name && email) {
        validSigners.push({
          name: name,
          email: email,
          role: signing2Designation,
          anchorStrings: ["/S3", "/S4GSD"],
        });
      }
    }

    // Add guarantors as signers — they sign the FTLA only (no GSD anchors)
    guarantors.forEach((g, idx) => {
      const anchor = ["/S6", "/S7", "/S8"][idx] || null;
      if (g.guaranteeType === "personal" && g.personalEmail) {
        validSigners.push({
          name: g.personalName,
          email: g.personalEmail,
          role: `Guarantor ${idx + 1}`,
          anchorStrings: anchor ? [anchor] : null,
        });
      } else if (g.guaranteeType === "company" && g.signatoryEmail) {
        validSigners.push({
          name: g.signatoryName,
          email: g.signatoryEmail,
          role: `Guarantor ${idx + 1} (Company)`,
          anchorStrings: anchor ? [anchor] : null,
        });
      }
    });

    // Add SimplyFunded lender signer if provided — signs both FTLA + GSD
    if (sfSigner.name && sfSigner.email) {
      validSigners.push({
        name: sfSigner.name,
        email: sfSigner.email,
        role: "lender_signer",
        title: sfSigner.title || "Director",
        anchorStrings: ["/S1", "/S1GSD"],
      });
    }

    if (validSigners.length === 0) {
      toast.error("Please add at least one signer with email address");
      return;
    }

    try {
      setSendingToDocuSign(true);
      
      // Build return URL for after tagging
      const returnUrl = `${window.location.origin}/aus/legal/documents?envelope_complete=true`;
      
      // Get application ID
      const applicationId = loanDetails.fundingAgreementNumber 
        ? `SFA-${loanDetails.fundingAgreementNumber}` 
        : `AUS-${Date.now()}`;

      // Build documents array
      const documents = [replacedDoc ? {
        type: "FTLA",
        file_base64: replacedDoc.file_base64,
        filename: replacedDoc.filename,
        content_type: replacedDoc.content_type,
      } : {
        type: "FTLA",
        file_url: generatedDoc.file_url,
        filename: `FTLA_${applicationId}.pdf`
      }];

      // Generate GSD if selected
      if (eSigningDocs.gsd) {
        try {
          toast.info("Generating General Security Deed...");
          
          // Get director name from selected director
          const directorName = selectedDirector 
            ? (selectedDirector.fixedDirector?.Name || selectedDirector.name || selectedDirector.email?.split('@')[0] || 'Director')
            : 'Director';
          
          const gsdResponse = await api.post('/aus/contracts/generate-gsd', {
            borrower_name: companyDetails?.company_name || companyDetails?.business_name || "Unknown Company",
            acn: companyDetails?.acn || companyDetails?.abn || "",
            director_name: directorName,
            date: new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }),
            // Same Lender-signer trio as the FTLA
            lender_signer_name: sfSigner.name || "Dean Morley",
            lender_signer_title: sfSigner.title || "Managing Director",
            lender_signer_authority: lenderSignerType === "dean_morley"
              ? ""
              : "Authorised Signatory under Power Of Attorney",
          });
          
          if (gsdResponse.data.success) {
            // Add GSD to documents
            documents.push({
              type: "GSD",
              file_base64: gsdResponse.data.document.file_base64,
              filename: gsdResponse.data.document.file_name,
              content_type: gsdResponse.data.document.content_type
            });
            toast.success("GSD generated successfully");
          } else {
            toast.error("Failed to generate GSD - continuing without it");
          }
        } catch (gsdError) {
          console.error("GSD generation error:", gsdError);
          toast.error("Failed to generate GSD - continuing without it");
        }
      }

      // Business Purpose Declaration (NCC s13) — mandatory for sole traders,
      // optional tick otherwise. It must be the FIRST document in the
      // envelope so it is signed before the loan contract.
      if (bpdIncluded) {
        try {
          toast.info("Generating Business Purpose Declaration...");
          const bpdResponse = await api.post('/aus/contracts/generate-business-purpose-declaration', {
            borrower_name: companyDetails?.company_name || companyDetails?.business_name || "",
            abn: companyDetails?.abn || "",
            acn: companyDetails?.acn || "",
            entity_type: companyDetails?.company_type || "",
            is_sole_trader: bpdRequired,
          });
          const bpdDoc = bpdResponse.data?.document;
          if (!bpdResponse.data?.success || !bpdDoc?.file_base64) throw new Error("No declaration file returned");
          documents.unshift({
            type: "Business Purpose Declaration",
            file_base64: bpdDoc.file_base64,
            filename: bpdDoc.file_name,
            content_type: bpdDoc.content_type,
          });
          toast.success("Business Purpose Declaration generated (first document in the envelope)");
        } catch (bpdError) {
          console.error("BPD generation error:", bpdError);
          const msg = bpdError.response?.data?.detail || bpdError.message || "unknown error";
          if (bpdRequired) {
            toast.error(`Business Purpose Declaration could not be generated (${msg}). It is mandatory for sole traders — envelope not sent.`);
            setSendingToDocuSign(false);
            return;
          }
          toast.error(`Business Purpose Declaration failed (${msg}) - continuing without it`);
        }
      }

      const response = await api.post('/contracts/send-to-docusign', {
        application_id: applicationId,
        company_name: companyDetails?.company_name || companyDetails?.business_name || "Unknown Company",
        documents: documents,
        signers: validSigners,
        email_subject: `SimplyFunded Australia - Loan Agreement - ${companyDetails?.company_name || "Customer"} - ${applicationId}`,
        email_message: `Please review and sign the attached loan agreement documents for ${companyDetails?.company_name || "your business"}. Reference: ${applicationId}`,
        sent_by: "legal_team",
        return_url: returnUrl,
        region: "aus",  // Use AUS brand for Australian documents
        borrower_entity: {
          abn: companyDetails?.abn || "",
          acn: companyDetails?.acn || "",
          entity_type: companyDetails?.company_type || "",
          is_sole_trader: bpdRequired,
          source: companyDetails?.source || "",
        },
      });
      
      if (response.data.success) {
        setDocuSignResult(response.data);
        
        // If we have a sender view URL, show it in iframe
        if (response.data.sender_view_url) {
          toast.success("Opening DocuSign tagging interface...");
          // Close the generation dialog BEFORE opening DocuSign iframe to avoid z-index conflicts
          setShowGenerateDialog(false);
          setDocuSignIframeUrl(response.data.sender_view_url);
          setShowDocuSignIframe(true);
        } else if (response.data.is_mock) {
          toast.info("DocuSign is in mock mode - configure credentials in Settings → API Credentials");
        } else {
          toast.success(`Draft envelope created! Envelope ID: ${response.data.envelope_id}`);
        }
      } else {
        toast.error("Failed to create DocuSign envelope");
      }
    } catch (error) {
      console.error("DocuSign error:", error);
      const detail = error.response?.data?.detail;
      toast.error((detail && typeof detail === "object" ? detail.message : detail) || "Failed to send to DocuSign");
    } finally {
      setSendingToDocuSign(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1CompanySelection();
      case 2:
        return renderStep2LoanDetails();
      case 3:
        return renderStep3BankDetails();
      case 4:
        return renderStep4Guarantors();
      case 5:
        return renderStep5Results();
      case 6:
        return renderStep6GoCardless();
      default:
        return null;
    }
  };

  // Step 1: Business Selection (ASIC + ABR lookup)
  const renderStep1CompanySelection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Search Australian Business</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Search for companies (ACN) via ASIC or sole traders/individuals (ABN) via ABR
        </p>
        
        <div className="flex gap-2 mb-4">
          <div className="flex-1">
            <Input
              placeholder="Search by business name, ACN or ABN..."
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchBusiness(companySearch)}
              data-testid="aus-company-search"
            />
          </div>
          <Button 
            onClick={() => searchBusiness(companySearch)}
            disabled={searchingCompany || companySearch.length < 2}
          >
            {searchingCompany ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        
        {!selectedCompany && Object.keys(searchRegisterErrors).length > 0 && (
          <div className="mb-3 p-3 border border-red-300 rounded-lg bg-red-50 text-sm text-red-800" data-testid="aus-search-register-error">
            <p className="font-medium flex items-center gap-1"><AlertCircle className="w-4 h-4" />A register did not answer — results below may be incomplete</p>
            {searchRegisterErrors.abr && <p className="text-xs mt-1"><b>ABR (sole traders / trusts):</b> {searchRegisterErrors.abr}. Admins: Portal Settings → API Credentials → ABR → Verify Connection.</p>}
            {searchRegisterErrors.asic && <p className="text-xs mt-1"><b>ASIC (companies):</b> {searchRegisterErrors.asic}</p>}
            {searchRegisterErrors.portal && <p className="text-xs mt-1">{searchRegisterErrors.portal}</p>}
          </div>
        )}
        {!selectedCompany && searchedOnce && !searchingCompany && companySearchResults.length === 0 && Object.keys(searchRegisterErrors).length === 0 && (
          <p className="mb-3 text-sm text-muted-foreground" data-testid="aus-search-no-results">No ASIC or ABR match for “{companySearch}”. Try the ABN or ACN on its own, or the individual's full name for a sole trader.</p>
        )}
        {/* Search Results */}
        {companySearchResults.length > 0 && !selectedCompany && (
          <div className="border rounded-lg max-h-60 overflow-y-auto">
            {companySearchResults.map((business, idx) => (
              <div 
                key={idx}
                className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                onClick={() => selectBusiness(business)}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{business.display_name || business.business_name || business.company_name}</span>
                  {business.is_sole_trader && (
                    <Badge variant="secondary" className="text-xs">Sole Trader</Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {business.acn && <span>ACN: {business.acn} • </span>}
                  {business.abn && <span>ABN: {business.abn} • </span>}
                  <span>{business.status}</span>
                  {business.entity_type && <span> • {business.entity_type}</span>}
                  <span className="ml-2 text-xs text-blue-600">({business.source})</span>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Manual Entry Option - shown when no results or user wants to enter manually */}
        {!selectedCompany && !showManualEntry && (
          <div className="mt-4 p-4 border border-dashed border-amber-300 rounded-lg bg-amber-50/50">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Business not found in ASIC/ABR?</p>
                <p className="text-xs text-amber-700 mt-1">
                  Some self-employed individuals or newly registered businesses may not appear in the search. 
                  You can enter the details manually.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2 border-amber-400 text-amber-700 hover:bg-amber-100"
                  onClick={() => setShowManualEntry(true)}
                  data-testid="aus-manual-entry-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Enter Details Manually
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Manual Entry Form */}
        {showManualEntry && !selectedCompany && (
          <Card className="border-2 border-amber-300 bg-amber-50/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4 text-amber-600" />
                    Manual Business Entry
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Enter business details for self-employed or businesses not found in ASIC/ABR
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowManualEntry(false)}
                >
                  Cancel
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="manual-company-name">Business/Trading Name *</Label>
                  <Input
                    id="manual-company-name"
                    value={manualBusinessEntry.company_name}
                    onChange={(e) => setManualBusinessEntry(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="e.g., John Smith Trading As..."
                    data-testid="aus-manual-company-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="manual-abn">ABN</Label>
                  <Input
                    id="manual-abn"
                    value={manualBusinessEntry.abn}
                    onChange={(e) => setManualBusinessEntry(prev => ({ ...prev, abn: e.target.value.replace(/\D/g, '').slice(0, 11) }))}
                    placeholder="11 digit ABN"
                    maxLength={11}
                    data-testid="aus-manual-abn"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Australian Business Number</p>
                </div>
                
                <div>
                  <Label htmlFor="manual-acn">ACN (optional)</Label>
                  <Input
                    id="manual-acn"
                    value={manualBusinessEntry.acn}
                    onChange={(e) => setManualBusinessEntry(prev => ({ ...prev, acn: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                    placeholder="9 digit ACN (if company)"
                    maxLength={9}
                    data-testid="aus-manual-acn"
                  />
                </div>
                
                <div>
                  <Label htmlFor="manual-entity-type">Entity Type</Label>
                  <Select 
                    value={manualBusinessEntry.entity_type} 
                    onValueChange={(value) => setManualBusinessEntry(prev => ({ ...prev, entity_type: value }))}
                  >
                    <SelectTrigger data-testid="aus-manual-entity-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sole Trader">Sole Trader</SelectItem>
                      <SelectItem value="Individual">Individual</SelectItem>
                      <SelectItem value="Partnership">Partnership</SelectItem>
                      <SelectItem value="Trust">Trust</SelectItem>
                      <SelectItem value="Company">Company (Pty Ltd)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="manual-status">Status</Label>
                  <Select 
                    value={manualBusinessEntry.status} 
                    onValueChange={(value) => setManualBusinessEntry(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger data-testid="aus-manual-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Registered">Registered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="manual-address">Business Address (optional)</Label>
                  <Input
                    id="manual-address"
                    value={manualBusinessEntry.registered_address}
                    onChange={(e) => setManualBusinessEntry(prev => ({ ...prev, registered_address: e.target.value }))}
                    placeholder="e.g., 123 Main St, Sydney NSW 2000"
                    data-testid="aus-manual-address"
                  />
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={applyManualBusinessEntry}
                  disabled={!manualBusinessEntry.company_name}
                  data-testid="aus-apply-manual-entry"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Apply Business Details
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Selected Business */}
        {selectedCompany && companyDetails && (
          <Card className={cn(
            "border-2",
            companyDetails.source === "Manual Entry"
              ? "border-amber-300 bg-amber-50"
              : companyDetails.is_sole_trader 
                ? "border-blue-200 bg-blue-50" 
                : "border-green-200 bg-green-50"
          )}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className={cn(
                      "w-5 h-5",
                      companyDetails.source === "Manual Entry"
                        ? "text-amber-600"
                        : companyDetails.is_sole_trader ? "text-blue-600" : "text-green-600"
                    )} />
                    <span className="font-semibold">{companyDetails.company_name}</span>
                    {companyDetails.is_sole_trader && (
                      <Badge variant="secondary">Sole Trader</Badge>
                    )}
                    {companyDetails.source === "Manual Entry" && (
                      <Badge variant="outline" className="border-amber-400 text-amber-700">Manual Entry</Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {companyDetails.acn && <p>ACN: {companyDetails.acn}</p>}
                    {companyDetails.abn && <p>ABN: {companyDetails.abn}</p>}
                    {!companyDetails.acn && !companyDetails.abn && <p>No ACN/ABN recorded</p>}
                    <p>Status: {companyDetails.status}</p>
                    <p>Type: {companyDetails.company_type}</p>
                    {companyDetails.registered_address && (
                      <p className="mt-1">{companyDetails.registered_address}</p>
                    )}
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setSelectedCompany(null);
                    setCompanyDetails(null);
                    setCompanySearchResults([]);
                  }}
                >
                  Change
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* SF Number */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-4">Agreement Reference</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sfNumber">SFA Number</Label>
            <div className="flex gap-2">
              <span className="flex items-center px-3 bg-gray-100 border border-r-0 rounded-l-md text-sm text-gray-600">SFA-</span>
              <Input
                id="sfNumber"
                type="text"
                value={loanDetails.fundingAgreementNumber}
                onChange={(e) => setLoanDetails(prev => ({ ...prev, fundingAgreementNumber: e.target.value.replace(/[^0-9]/g, '') }))}
                placeholder="e.g., 15740"
                className="rounded-l-none"
                data-testid="aus-sf-number"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="agreementDate">Agreement Date</Label>
            <Input
              id="agreementDate"
              type="date"
              value={loanDetails.agreementDate}
              onChange={(e) => setLoanDetails(prev => ({ ...prev, agreementDate: e.target.value }))}
              data-testid="aus-agreement-date"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Step 2: Loan Details
  const renderStep2LoanDetails = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Loan Details</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="commencementDate">Commencement Date</Label>
          <Input
            id="commencementDate"
            type="date"
            value={loanDetails.commencementDate}
            onChange={(e) => setLoanDetails(prev => ({ ...prev, commencementDate: e.target.value }))}
            data-testid="aus-commencement-date"
          />
        </div>
        
        <div>
          <Label htmlFor="loanAmount">Loan Amount ($)</Label>
          <Input
            id="loanAmount"
            type="number"
            value={loanDetails.loanAmount || ""}
            onChange={(e) => setLoanDetails(prev => ({ ...prev, loanAmount: parseFloat(e.target.value) || 0 }))}
            placeholder="e.g., 50000"
            data-testid="aus-loan-amount"
          />
        </div>
        
        <div>
          <Label htmlFor="factorRate">Factor Rate</Label>
          <Input
            id="factorRate"
            type="number"
            step="0.01"
            min="1.0"
            max="2.0"
            value={loanDetails.factorRate}
            onChange={(e) => setLoanDetails(prev => ({ ...prev, factorRate: parseFloat(e.target.value) || 1.5 }))}
            data-testid="aus-factor-rate"
          />
          <p className="text-xs text-muted-foreground mt-1">Default 1.50</p>
        </div>
        
        <div>
          <Label htmlFor="termInWeeks">Term (weeks)</Label>
          <Input
            id="termInWeeks"
            type="number"
            value={loanDetails.termInWeeks}
            onChange={(e) => setLoanDetails(prev => ({ ...prev, termInWeeks: parseInt(e.target.value) || 26 }))}
            data-testid="aus-term-weeks"
          />
        </div>
        
        <div>
          <Label htmlFor="paymentFrequency">Payment Frequency</Label>
          <Select 
            value={loanDetails.paymentFrequency} 
            onValueChange={(value) => setLoanDetails(prev => ({ ...prev, paymentFrequency: value }))}
          >
            <SelectTrigger data-testid="aus-payment-frequency">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Daily">Daily</SelectItem>
              <SelectItem value="Weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Payment Day - only show for weekly payments */}
        {loanDetails.paymentFrequency === "Weekly" && (
          <div>
            <Label htmlFor="paymentDay">Payment Day</Label>
            <Select 
              value={loanDetails.paymentDay} 
              onValueChange={(value) => setLoanDetails(prev => ({ ...prev, paymentDay: value }))}
            >
              <SelectTrigger data-testid="aus-payment-day">
                <SelectValue placeholder="Select day" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Monday">Monday</SelectItem>
                <SelectItem value="Tuesday">Tuesday</SelectItem>
                <SelectItem value="Wednesday">Wednesday</SelectItem>
                <SelectItem value="Thursday">Thursday</SelectItem>
                <SelectItem value="Friday">Friday</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        
        <div>
          <Label htmlFor="arrangementFee">Arrangement Fee ($)</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {[0, 195, 495, 2995].map((fee) => (
              <Button
                key={fee}
                type="button"
                variant={loanDetails.arrangementFee === fee ? "default" : "outline"}
                size="sm"
                className="text-xs"
                onClick={() => setLoanDetails(prev => ({ ...prev, arrangementFee: fee }))}
                data-testid={`aus-arrangement-fee-${fee}`}
              >
                {fee === 0 ? "$0" : `$${fee.toLocaleString()}`}
              </Button>
            ))}
            <Button
              type="button"
              variant={![0, 195, 495, 2995].includes(loanDetails.arrangementFee) && loanDetails.arrangementFee > 0 ? "default" : "outline"}
              size="sm"
              className="text-xs"
              onClick={() => setLoanDetails(prev => ({ ...prev, arrangementFee: -1 }))}
              data-testid="aus-arrangement-fee-other"
            >
              Other
            </Button>
          </div>
          {(![0, 195, 495, 2995].includes(loanDetails.arrangementFee) && loanDetails.arrangementFee !== 0) && (
            <Input
              id="arrangementFee"
              type="number"
              min="0"
              step="0.01"
              placeholder="Enter custom amount"
              value={loanDetails.arrangementFee === -1 ? "" : (loanDetails.arrangementFee || "")}
              onChange={(e) => setLoanDetails(prev => ({ ...prev, arrangementFee: parseFloat(e.target.value) || 0 }))}
              data-testid="aus-arrangement-fee"
            />
          )}
        </div>
        
        <div>
          <Label htmlFor="legalFee">Legal Fee ($)</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {[0, 195, 495, 2995].map((fee) => (
              <Button
                key={fee}
                type="button"
                variant={loanDetails.legalFee === fee ? "default" : "outline"}
                size="sm"
                className="text-xs"
                onClick={() => setLoanDetails(prev => ({ ...prev, legalFee: fee }))}
                data-testid={`aus-legal-fee-${fee}`}
              >
                {fee === 0 ? "$0" : `$${fee.toLocaleString()}`}
              </Button>
            ))}
            <Button
              type="button"
              variant={![0, 195, 495, 2995].includes(loanDetails.legalFee) && loanDetails.legalFee > 0 ? "default" : "outline"}
              size="sm"
              className="text-xs"
              onClick={() => setLoanDetails(prev => ({ ...prev, legalFee: -1 }))}
              data-testid="aus-legal-fee-other"
            >
              Other
            </Button>
          </div>
          {(![0, 195, 495, 2995].includes(loanDetails.legalFee) && loanDetails.legalFee !== 0) && (
            <Input
              id="legalFee"
              type="number"
              min="0"
              step="0.01"
              placeholder="Enter custom amount"
              value={loanDetails.legalFee === -1 ? "" : (loanDetails.legalFee || "")}
              onChange={(e) => setLoanDetails(prev => ({ ...prev, legalFee: parseFloat(e.target.value) || 0 }))}
              data-testid="aus-legal-fee"
            />
          )}
        </div>
        
        <div>
          <Label htmlFor="missedPaymentFee">Missed Payment Fee ($/day)</Label>
          <Input
            id="missedPaymentFee"
            type="number"
            min="0"
            step="0.01"
            value={loanDetails.missedPaymentFee}
            onChange={(e) => setLoanDetails(prev => ({ ...prev, missedPaymentFee: parseFloat(e.target.value) || 0 }))}
            data-testid="aus-missed-payment-fee"
          />
          <p className="text-xs text-muted-foreground mt-1">Default $20/day</p>
        </div>
      </div>
      
      {/* Auto-calculated summary */}
      {loanDetails.loanAmount > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Auto-Calculated Values
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Factor Amount</p>
                <p className="font-semibold">{formatCurrency(calculatedFields.factorAmount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Upfront Fees</p>
                <p className="font-semibold">{formatCurrency(calculatedFields.upfrontFees)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Repayment</p>
                <p className="font-semibold text-blue-600">{formatCurrency(calculatedFields.totalLoanAmount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Payment Amount</p>
                <p className="font-semibold">{formatCurrency(calculatedFields.paymentAmount)}</p>
                <p className="text-xs text-muted-foreground">
                  ({calculatedFields.numPayments} payments)
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Maturity Date</p>
                <p className="font-semibold">{formatDate(calculatedFields.maturityDate) || "Set commencement date"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Default Fee (10%)</p>
                <p className="font-semibold">{formatCurrency(calculatedFields.defaultFee)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-muted-foreground">Payment Days</p>
                <p className="font-semibold">{calculatedFields.paymentDays}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Postcode lookup function
  const lookupPostcode = async (postcode) => {
    if (!postcode || postcode.length !== 4) return;
    try {
      const response = await api.get(`/aus/address/postcode/${postcode}`);
      if (response.data.success) {
        setBorrowerAddress(prev => ({
          ...prev,
          state: response.data.state || prev.state,
          suburb: response.data.locality || prev.suburb
        }));
      }
    } catch (error) {
      console.error("Postcode lookup error:", error);
    }
  };

  // Address autocomplete search
  const searchAddresses = async (query, postcode = "") => {
    if (!query || query.length < 3) {
      setAddressResults([]);
      return;
    }
    
    try {
      setSearchingAddress(true);
      const params = new URLSearchParams({ q: query });
      if (postcode) params.append("postcode", postcode);
      
      const response = await api.get(`/aus/address/autocomplete?${params.toString()}`);
      if (response.data.success) {
        setAddressResults(response.data.results || []);
      }
    } catch (error) {
      console.error("Address search error:", error);
      setAddressResults([]);
    } finally {
      setSearchingAddress(false);
    }
  };

  // Select address from autocomplete results
  const selectAddress = (address) => {
    setBorrowerAddress({
      addressLine1: address.street_line1 || address.full_address?.split(",")[0] || "",
      addressLine2: address.street_line2 || "",
      suburb: address.locality || "",
      state: address.state || "",
      postcode: address.postcode || ""
    });
    setAddressResults([]);
    setAddressSearch("");
  };

  // Format full address
  const getFormattedAddress = () => {
    const parts = [
      borrowerAddress.addressLine1,
      borrowerAddress.addressLine2,
      borrowerAddress.suburb,
      borrowerAddress.state,
      borrowerAddress.postcode
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Step 3: Bank Details & Signatories
  const renderStep3BankDetails = () => (
    <div className="space-y-6">
      {/* Borrower Address Section */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Borrower Address</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Search for address or enter manually
        </p>
        
        {/* Address Search */}
        <div className="mb-4">
          <Label>Search Address</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Enter postcode (optional)"
              value={borrowerAddress.postcode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                setBorrowerAddress(prev => ({ ...prev, postcode: value }));
              }}
              className="w-32"
              maxLength={4}
            />
            <div className="flex-1 relative">
              <Input
                placeholder="Start typing street address..."
                value={addressSearch}
                onChange={(e) => {
                  setAddressSearch(e.target.value);
                  searchAddresses(e.target.value, borrowerAddress.postcode);
                }}
                data-testid="aus-address-search"
              />
              {searchingAddress && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
          
          {/* Address autocomplete results */}
          {addressResults.length > 0 && (
            <div className="border rounded-lg mt-2 max-h-48 overflow-y-auto bg-white shadow-lg">
              {addressResults.map((addr, idx) => (
                <div 
                  key={idx}
                  className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 text-sm"
                  onClick={() => selectAddress(addr)}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span>{addr.display || addr.full_address}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <Separator className="my-4" />
        
        <p className="text-sm text-muted-foreground mb-2">Or enter manually:</p>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="addressLine1">Address Line 1</Label>
              <Input
                id="addressLine1"
                value={borrowerAddress.addressLine1}
                onChange={(e) => setBorrowerAddress(prev => ({ ...prev, addressLine1: e.target.value }))}
                placeholder="Unit/Level, Street number and name"
                data-testid="aus-address-line1"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="addressLine2">Address Line 2 (optional)</Label>
              <Input
                id="addressLine2"
                value={borrowerAddress.addressLine2}
                onChange={(e) => setBorrowerAddress(prev => ({ ...prev, addressLine2: e.target.value }))}
                placeholder="Building name, etc."
                data-testid="aus-address-line2"
              />
            </div>
            <div>
              <Label htmlFor="suburb">Suburb</Label>
              <Input
                id="suburb"
                value={borrowerAddress.suburb}
                onChange={(e) => setBorrowerAddress(prev => ({ ...prev, suburb: e.target.value }))}
                placeholder="e.g., Melbourne"
                data-testid="aus-suburb"
              />
            </div>
            <div>
              <Label htmlFor="ausState">State</Label>
              <Select 
                value={borrowerAddress.state} 
                onValueChange={(value) => setBorrowerAddress(prev => ({ ...prev, state: value }))}
              >
                <SelectTrigger data-testid="aus-state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NSW">New South Wales</SelectItem>
                  <SelectItem value="VIC">Victoria</SelectItem>
                  <SelectItem value="QLD">Queensland</SelectItem>
                  <SelectItem value="SA">South Australia</SelectItem>
                  <SelectItem value="WA">Western Australia</SelectItem>
                  <SelectItem value="TAS">Tasmania</SelectItem>
                  <SelectItem value="NT">Northern Territory</SelectItem>
                  <SelectItem value="ACT">Australian Capital Territory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="postcode">Postcode</Label>
              <Input
                id="postcode"
                value={borrowerAddress.postcode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setBorrowerAddress(prev => ({ ...prev, postcode: value }));
                }}
                onBlur={() => lookupPostcode(borrowerAddress.postcode)}
                placeholder="e.g., 3000"
                maxLength={4}
                data-testid="aus-postcode"
              />
              <p className="text-xs text-muted-foreground mt-1">Auto-fills state when entered</p>
            </div>
          </div>
        </div>
      </div>
      
      <Separator />
      
      <div>
        <h3 className="text-lg font-semibold mb-4">Borrower Bank Details</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="borrowerAccountName">Account Name</Label>
            <Input
              id="borrowerAccountName"
              value={borrowerBankDetails.accountName}
              onChange={(e) => setBorrowerBankDetails(prev => ({ ...prev, accountName: e.target.value }))}
              placeholder="Company name"
              data-testid="aus-borrower-account-name"
            />
          </div>
          <div>
            <Label htmlFor="borrowerBsb">BSB</Label>
            <Input
              id="borrowerBsb"
              value={borrowerBankDetails.bsb}
              onChange={(e) => setBorrowerBankDetails(prev => ({ ...prev, bsb: e.target.value }))}
              placeholder="000-000"
              data-testid="aus-borrower-bsb"
            />
          </div>
          <div>
            <Label htmlFor="borrowerAccountNumber">Account Number</Label>
            <Input
              id="borrowerAccountNumber"
              value={borrowerBankDetails.accountNumber}
              onChange={(e) => setBorrowerBankDetails(prev => ({ ...prev, accountNumber: e.target.value }))}
              placeholder="123456789"
              data-testid="aus-borrower-account-number"
            />
          </div>
        </div>
      </div>
      
      <Separator />
      
      <div>
        <h3 className="text-lg font-semibold mb-2">Borrower Email for Service of Legal Process</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Per clause 16.5 of the FTLA — legal process may be served at this email. Pre-fills from the
          first signing director's email; edit if a different address should be used.
        </p>
        <div className="max-w-md">
          <Label htmlFor="borrowerServiceEmail">Email for service</Label>
          <Input
            id="borrowerServiceEmail"
            type="email"
            value={borrowerServiceEmail}
            onChange={(e) => setBorrowerServiceEmail(e.target.value)}
            placeholder={
              (typeof signingDirector1 === 'object' && signingDirector1?.email)
                || "service@borrower.com.au"
            }
            data-testid="aus-borrower-service-email"
          />
        </div>
      </div>
      
      <Separator />
      
      <div>
        <CapayAccountCard
          sfNumber={(() => { const d = (loanDetails.fundingAgreementNumber || "").replace(/^(sfa-?)+/i, "").trim(); return d ? `SFA-${d}` : ""; })()}
          companyDetails={companyDetails}
          borrowerAddress={borrowerAddress}
          signingDirector={signingDirector1}
          contactEmail={borrowerServiceEmail}
          user={user}
          onAccount={setCapayAccount}
          choice={settlementChoice}
          setChoice={setSettlementChoice}
          onOptions={setSettlementOptions}
        />
        <details className="mt-3 text-xs text-muted-foreground" data-testid="aus-house-account-details">
          <summary className="cursor-pointer">CaPay house account (Morley Capital Australia) — reference only</summary>
          {loadingLenderBank ? (
            <div className="flex items-center gap-2 mt-2"><Loader2 className="w-3 h-3 animate-spin" />Loading...</div>
          ) : lenderBankDetails ? (
            <p className="mt-2">{lenderBankDetails.name} · BSB {lenderBankDetails.bsb} · {lenderBankDetails.accountNumber} · {lenderBankDetails.bankName}</p>
          ) : (
            <p className="mt-2 text-destructive">
              {lenderBankError || "No lender bank account is marked as currently in use."}
            </p>
          )}
        </details>
      </div>
      
      <Separator />
      
      <div>
        <h3 className="text-lg font-semibold mb-4">Signing Directors</h3>
        
        {/* Director Selection Panel */}
        <Card className="mb-4 border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  Select Verified Director
                </CardTitle>
                <CardDescription className="text-xs">
                  Choose from recent directors or search/add new ones
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowAddDirectorDialog(true)}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add New
                </Button>
                {sumsubStatus?.configured && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowSyncDialog(true)}
                  >
                    Sync from Sumsub
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search Input */}
            <div className="mb-3">
              <Input
                placeholder="Search directors by name or email..."
                value={directorSearch}
                onChange={(e) => {
                  setDirectorSearch(e.target.value);
                  searchDirectors(e.target.value);
                }}
                className="w-full"
                data-testid="director-search-input"
              />
            </div>
            
            {/* Recent Directors List */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {loadingDirectors ? (
                <div className="flex items-center justify-center py-4 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Loading directors...
                </div>
              ) : recentDirectors.length > 0 ? (
                recentDirectors.map((director, idx) => (
                  <div
                    key={director.sumsub_applicant_id || idx}
                    className={`p-3 border rounded-lg cursor-pointer transition-all hover:bg-blue-100 group ${
                      selectedDirector?.sumsub_applicant_id === director.sumsub_applicant_id 
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                        : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => selectDirector(director)}
                    data-testid={`director-option-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{director.full_name}</span>
                          <Badge 
                            variant={director.verification_status === "completed" ? "success" : director.verification_status === "manual_entry" ? "outline" : "secondary"}
                            className="text-xs"
                          >
                            {director.verification_status === "manual_entry" ? "Manual" : director.verification_status || "Verified"}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 space-x-3">
                          {director.email ? (
                            <span>{director.email}</span>
                          ) : (
                            <span className="text-amber-600">No email in Sumsub</span>
                          )}
                          {director.phone && <span>• {director.phone}</span>}
                        </div>
                        {director.address && (
                          <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-xs">
                            {director.address}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedDirector?.sumsub_applicant_id === director.sumsub_applicant_id && (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDirector(director.sumsub_applicant_id);
                          }}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  <p>No directors found.</p>
                  <p className="text-xs mt-1">Add one manually or sync from Sumsub.</p>
                </div>
              )}
            </div>
            
            {/* Selected Director Actions */}
            {selectedDirector && (
              <div className="mt-3 pt-3 border-t space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Selected:</span>{" "}
                    <span className="font-medium">{selectedDirector.full_name}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="default" onClick={() => applyDirectorToField("director", 1)}>
                      Set as Director 1
                    </Button>
                    {addSecondDirector && (
                      <Button size="sm" variant="outline" onClick={() => applyDirectorToField("director", 2)}>
                        Set as Director 2
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Show director details and allow email entry if missing */}
                <div className="text-xs space-y-1 bg-gray-50 p-2 rounded">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Address:</span>
                    <span>{selectedDirector.address || "Not available"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Email:</span>
                    {selectedDirector.email ? (
                      <span>{selectedDirector.email}</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Input
                          className="h-6 text-xs w-48"
                          placeholder="Enter email manually"
                          value={selectedDirector.manualEmail || ""}
                          onChange={(e) => {
                            setSelectedDirector({
                              ...selectedDirector,
                              email: e.target.value,
                              manualEmail: e.target.value
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Sync from Sumsub Dialog */}
        <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Sync Director from Sumsub</DialogTitle>
              <DialogDescription>
                Enter the Sumsub External User ID to fetch verified director details.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>External User ID</Label>
                <Input
                  value={sumsubExternalId}
                  onChange={(e) => setSumsubExternalId(e.target.value)}
                  placeholder="e.g., user_12345"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && sumsubExternalId) {
                      syncDirectorFromSumsub(sumsubExternalId);
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This is the ID used when the applicant completed their Sumsub verification.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSyncDialog(false)}>Cancel</Button>
              <Button 
                onClick={() => syncDirectorFromSumsub(sumsubExternalId)}
                disabled={!sumsubExternalId || loadingDirectors}
              >
                {loadingDirectors ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Sync Director
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Add Director Dialog */}
        <Dialog open={showAddDirectorDialog} onOpenChange={setShowAddDirectorDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Director</DialogTitle>
              <DialogDescription>
                Enter director details manually. For verified directors, sync from Sumsub.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={newDirectorForm.full_name}
                  onChange={(e) => setNewDirectorForm(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newDirectorForm.email}
                  onChange={(e) => setNewDirectorForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newDirectorForm.phone}
                  onChange={(e) => setNewDirectorForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+61 400 000 000"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={newDirectorForm.address}
                  onChange={(e) => setNewDirectorForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Main St, Sydney NSW 2000"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDirectorDialog(false)}>Cancel</Button>
              <Button onClick={addDirectorManually}>Add Director</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Director 1 Name</Label>
              <Input
                value={typeof signingDirector1 === 'object' ? signingDirector1?.full_name || signingDirector1?.name || "" : signingDirector1 || ""}
                onChange={(e) => setSigningDirector1({ full_name: e.target.value, name: e.target.value, email: "" })}
                placeholder="Full name"
                data-testid="aus-director1-name"
              />
            </div>
            <div>
              <Label>Director 1 Email</Label>
              <Input
                value={typeof signingDirector1 === 'object' ? signingDirector1?.email || "" : ""}
                onChange={(e) => setSigningDirector1(prev => ({ 
                  ...(typeof prev === 'object' ? prev : { full_name: prev, name: prev }), 
                  email: e.target.value 
                }))}
                placeholder="Email address"
                data-testid="aus-director1-email"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Designation</Label>
              <Select value={signing1Designation} onValueChange={setSigning1Designation}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Director">Director</SelectItem>
                  <SelectItem value="Secretary">Secretary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="addSecondDirector" 
              checked={addSecondDirector}
              onCheckedChange={setAddSecondDirector}
            />
            <Label htmlFor="addSecondDirector" className="cursor-pointer">Add second signatory</Label>
          </div>
          
          {addSecondDirector && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Director/Secretary 2 Name</Label>
                <Input
                  value={typeof signingDirector2 === 'object' ? signingDirector2?.full_name || signingDirector2?.name || "" : signingDirector2 || ""}
                  onChange={(e) => setSigningDirector2({ full_name: e.target.value, name: e.target.value, email: "" })}
                  placeholder="Full name"
                  data-testid="aus-director2-name"
                />
              </div>
              <div>
                <Label>Director/Secretary 2 Email</Label>
                <Input
                  value={typeof signingDirector2 === 'object' ? signingDirector2?.email || "" : ""}
                  onChange={(e) => setSigningDirector2(prev => ({ 
                    ...(typeof prev === 'object' ? prev : { full_name: prev, name: prev }), 
                    email: e.target.value 
                  }))}
                  placeholder="Email address"
                  data-testid="aus-director2-email"
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Select value={signing2Designation} onValueChange={setSigning2Designation}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Director">Director</SelectItem>
                    <SelectItem value="Secretary">Secretary</SelectItem>
                    <SelectItem value="Director/Secretary">Director/Secretary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Step 4: Guarantors
  const renderStep4Guarantors = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Guarantors</h3>
        {guarantors.length < 3 && (
          <Button variant="outline" size="sm" onClick={addGuarantor}>
            <Plus className="w-4 h-4 mr-1" />
            Add Guarantor
          </Button>
        )}
      </div>
      
      <p className="text-sm text-muted-foreground">
        Add guarantors for this loan. Choose whether each guarantee is from an individual (personal) or a company.
      </p>
      
      {guarantors.map((guarantor, index) => (
        <Card key={index}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Guarantor {index + 1}</CardTitle>
              <div className="flex gap-2">
                {selectedDirector && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyDirectorToGuarantor(index)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <User className="w-3 h-3 mr-1" />
                    Use Director Info
                  </Button>
                )}
                {index > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-600 hover:text-red-700"
                    onClick={() => removeGuarantor(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            {guarantor.useDirectorInfo && selectedDirector && (
              <div className="text-xs text-blue-600 mt-1">
                Using director info from: {selectedDirector.full_name}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Guarantee Type Selection */}
              <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`guaranteeType-${index}`}
                    value="personal"
                    checked={guarantor.guaranteeType === "personal"}
                    onChange={() => updateGuarantor(index, 'guaranteeType', 'personal')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium">Personal Guarantee</span>
                  <span className="text-xs text-muted-foreground">(Individual)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`guaranteeType-${index}`}
                    value="company"
                    checked={guarantor.guaranteeType === "company"}
                    onChange={() => updateGuarantor(index, 'guaranteeType', 'company')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium">Company Guarantee</span>
                  <span className="text-xs text-muted-foreground">(Corporate)</span>
                </label>
              </div>
              
              {/* Personal Guarantee Fields */}
              {guarantor.guaranteeType === "personal" && (
                <div className="grid grid-cols-1 gap-4 pt-2">
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                    <strong>Personal Guarantee:</strong> An individual is personally guaranteeing the loan.
                  </div>
                  <div>
                    <Label>Guarantor Full Name *</Label>
                    <Input
                      value={guarantor.personalName}
                      onChange={(e) => updateGuarantor(index, 'personalName', e.target.value)}
                      placeholder="John Smith"
                      data-testid={`aus-guarantor-${index}-personal-name`}
                    />
                  </div>
                  <div>
                    <Label>Residential Address *</Label>
                    <Input
                      value={guarantor.personalAddress}
                      onChange={(e) => updateGuarantor(index, 'personalAddress', e.target.value)}
                      placeholder="123 Main Street, Sydney NSW 2000"
                      data-testid={`aus-guarantor-${index}-personal-address`}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={guarantor.personalEmail || ""}
                      onChange={(e) => updateGuarantor(index, 'personalEmail', e.target.value)}
                      placeholder="john@example.com"
                      data-testid={`aus-guarantor-${index}-personal-email`}
                    />
                  </div>
                  <div>
                    <Label>Email for service of legal process</Label>
                    <Input
                      type="email"
                      value={guarantor.serviceEmail || guarantor.personalEmail || ""}
                      onChange={(e) => updateGuarantor(index, 'serviceEmail', e.target.value)}
                      placeholder={guarantor.personalEmail || "service@example.com"}
                      data-testid={`aus-guarantor-${index}-service-email`}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Pre-filled from the guarantor's email — edit if legal service should go to a different address (clause 16.5).
                    </p>
                  </div>
                </div>
              )}
              
              {/* Company Guarantee Fields */}
              {guarantor.guaranteeType === "company" && (
                <div className="grid grid-cols-1 gap-4 pt-2">
                  <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                    <strong>Company Guarantee:</strong> A company is guaranteeing the loan. You'll need the company details and the person signing on behalf of the company.
                  </div>
                  
                  <Separator />
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Company Details</p>
                  
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      value={guarantor.companyName}
                      onChange={(e) => updateGuarantor(index, 'companyName', e.target.value)}
                      placeholder="ABC Pty Ltd"
                      data-testid={`aus-guarantor-${index}-company-name`}
                    />
                  </div>
                  <div>
                    <Label>Registered Address *</Label>
                    <Input
                      value={guarantor.companyAddress}
                      onChange={(e) => updateGuarantor(index, 'companyAddress', e.target.value)}
                      placeholder="Level 1, 100 George Street, Sydney NSW 2000"
                      data-testid={`aus-guarantor-${index}-company-address`}
                    />
                  </div>
                  
                  <Separator />
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Authorised Signatory</p>
                  
                  <div>
                    <Label>Signatory Name *</Label>
                    <Input
                      value={guarantor.signatoryName}
                      onChange={(e) => updateGuarantor(index, 'signatoryName', e.target.value)}
                      placeholder="Name of director/secretary signing"
                      data-testid={`aus-guarantor-${index}-signatory-name`}
                    />
                  </div>
                  <div>
                    <Label>Signatory Email</Label>
                    <Input
                      value={guarantor.signatoryEmail || ""}
                      onChange={(e) => updateGuarantor(index, 'signatoryEmail', e.target.value)}
                      placeholder="signatory@company.com"
                      data-testid={`aus-guarantor-${index}-signatory-email`}
                    />
                  </div>
                  <div>
                    <Label>Email for service of legal process</Label>
                    <Input
                      type="email"
                      value={guarantor.serviceEmail || guarantor.signatoryEmail || ""}
                      onChange={(e) => updateGuarantor(index, 'serviceEmail', e.target.value)}
                      placeholder={guarantor.signatoryEmail || "service@company.com"}
                      data-testid={`aus-guarantor-${index}-service-email`}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Pre-filled from the signatory's email — edit if legal service should go to a different address (clause 16.5).
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
      
      {/* SimplyFunded Lender Signer Section */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <Building className="w-4 h-4 text-white" />
              </div>
              <div>
                <CardTitle className="text-sm">SimplyFunded Australia - Lender Signatory</CardTitle>
                <p className="text-xs text-muted-foreground">Person signing on behalf of the lender</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-blue-600 border-blue-200 hover:bg-blue-100"
              onClick={() => {
                if (user) {
                  setLenderSignerType("me");
                  setSfSigner({
                    name: user.name || '',
                    email: user.email || '',
                    title: 'Director'
                  });
                  toast.success("Filled with your details");
                } else {
                  toast.error("Please log in to use this feature");
                }
              }}
              data-testid="aus-sf-signer-fill-me"
            >
              <UserCheck className="w-4 h-4 mr-1" />
              That's Me
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Signer role dropdown — controls Dean/Me/Other + POA line on the docs */}
          <div>
            <Label className="text-xs text-muted-foreground">Who will sign on behalf of the Lender?</Label>
            <Select
              value={lenderSignerType}
              onValueChange={(val) => {
                setLenderSignerType(val);
                if (val === "dean_morley") {
                  setSfSigner({ name: "Dean Morley", email: "dean.morley@simplyfunded.co.uk", title: "Managing Director" });
                } else if (val === "me") {
                  setSfSigner({
                    name: user?.name || "",
                    email: user?.email || "",
                    title: sfSigner.title && sfSigner.title !== "Managing Director" ? sfSigner.title : "Director",
                  });
                } else {
                  // "other" — clear fields for manual entry
                  setSfSigner({ name: "", email: "", title: "" });
                }
              }}
            >
              <SelectTrigger data-testid="aus-lender-signer-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dean_morley" data-testid="aus-lender-signer-dean">Dean Morley (Managing Director)</SelectItem>
                <SelectItem value="me" data-testid="aus-lender-signer-me">Me (my portal account)</SelectItem>
                <SelectItem value="other" data-testid="aus-lender-signer-other">Someone else (enter manually)</SelectItem>
              </SelectContent>
            </Select>
            {lenderSignerType !== "dean_morley" && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                <strong>“Authorised Signatory under Power Of Attorney”</strong> will be added beneath the title on the signature block.
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Name *</Label>
              <Input
                placeholder="e.g., Dean Morley"
                value={sfSigner.name}
                onChange={(e) => setSfSigner(prev => ({ ...prev, name: e.target.value }))}
                disabled={lenderSignerType === "dean_morley"}
                data-testid="aus-sf-signer-name"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Email *</Label>
              <Input
                type="email"
                placeholder="e.g., dean@simplyfund.com.au"
                value={sfSigner.email}
                onChange={(e) => setSfSigner(prev => ({ ...prev, email: e.target.value }))}
                disabled={lenderSignerType === "dean_morley"}
                data-testid="aus-sf-signer-email"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Title</Label>
              <Input
                placeholder="e.g., Director"
                value={sfSigner.title}
                onChange={(e) => setSfSigner(prev => ({ ...prev, title: e.target.value }))}
                disabled={lenderSignerType === "dean_morley"}
                data-testid="aus-sf-signer-title"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Step 5: Results & E-Signing
  const renderStep5Results = () => (
    <div className="space-y-6">
      {generatedDoc ? (
        <>
          {/* Success Header */}
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Document Generated!</h3>
            <p className="text-muted-foreground">
              Your Fixed Term Loan Agreement has been generated successfully.
            </p>
          </div>
          
          {/* Generated Document Card */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium">Fixed Term Loan Agreement (AUS)</p>
                    <p className="text-sm text-muted-foreground">
                      {loanDetails.fundingAgreementNumber.startsWith('SFA-') 
                        ? loanDetails.fundingAgreementNumber 
                        : `SFA-${loanDetails.fundingAgreementNumber}`} • {companyDetails?.company_name}
                      {companyDetails?.is_sole_trader && (
                        <Badge variant="secondary" className="ml-2 text-xs">Sole Trader</Badge>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {!replacedDoc && (
                    <Button asChild>
                      <a href={generatedDoc.file_url} target="_blank" rel="noopener noreferrer">
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                      </a>
                    </Button>
                  )}
                  {!replacedDoc && (
                    <Button variant="outline" onClick={downloadAsWord} disabled={wordBusy} title="Download as an editable Word file" data-testid="aus-doc-word">
                      {wordBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Word
                    </Button>
                  )}
                  <label className="inline-flex items-center h-10 px-4 rounded-md border text-sm cursor-pointer hover:bg-slate-50"
                    title="Replace with your edited PDF/Word — the replacement goes to DocuSign" data-testid="aus-doc-replace">
                    <Download className="w-4 h-4 mr-2 rotate-180" /> {replacedDoc ? "Replace again" : "Replace"}
                    <input type="file" accept=".pdf,.docx" className="hidden" onChange={handleReplaceDoc} data-testid="aus-doc-replace-input" />
                  </label>
                  {replacedDoc && (
                    <Button variant="ghost" onClick={() => setReplacedDoc(null)} data-testid="aus-doc-revert">Revert</Button>
                  )}
                </div>
              </div>
              {replacedDoc && (
                <p className="text-xs text-amber-800 mt-2" data-testid="aus-doc-replaced-note">✎ DocuSign will receive <strong>{replacedDoc.filename}</strong> instead of the generated PDF.</p>
              )}
            </CardContent>
          </Card>
          
          <Separator />
          
          {/* E-Signing Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              E-Signing via DocuSign
            </h3>
            
            {!showDocuSignIframe ? (
              <>
                {/* DocuSign Send Option */}
                <Card className="mb-4">
                  <CardContent className="p-6">
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                        <FileSignature className="w-8 h-8 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Send for E-Signature</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          Send the document to DocuSign for electronic signature. 
                          Emails will be sent from your Australian domain.
                        </p>
                      </div>
                      
                      {/* Signers Summary */}
                      <div className="text-left bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-sm font-medium mb-2">Signers:</p>
                        <div className="space-y-1 text-sm">
                          {signingDirector1 && (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-green-600" />
                              <span>
                                {typeof signingDirector1 === 'object' 
                                  ? (signingDirector1.name || signingDirector1.full_name) 
                                  : signingDirector1} ({signing1Designation})
                              </span>
                              {typeof signingDirector1 === 'object' && signingDirector1.email && (
                                <span className="text-muted-foreground">- {signingDirector1.email}</span>
                              )}
                            </div>
                          )}
                          {addSecondDirector && signingDirector2 && (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-green-600" />
                              <span>
                                {typeof signingDirector2 === 'object' 
                                  ? (signingDirector2.name || signingDirector2.full_name) 
                                  : signingDirector2} ({signing2Designation})
                              </span>
                              {typeof signingDirector2 === 'object' && signingDirector2.email && (
                                <span className="text-muted-foreground">- {signingDirector2.email}</span>
                              )}
                            </div>
                          )}
                          {guarantors.map((g, idx) => {
                            if (g.guaranteeType === "personal" && g.personalEmail) {
                              return (
                                <div key={idx} className="flex items-center gap-2">
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  <span>{g.personalName} (Guarantor {idx + 1})</span>
                                  <span className="text-muted-foreground">- {g.personalEmail}</span>
                                </div>
                              );
                            } else if (g.guaranteeType === "company" && g.signatoryEmail) {
                              return (
                                <div key={idx} className="flex items-center gap-2">
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  <span>{g.signatoryName} (Guarantor {idx + 1} - Company)</span>
                                  <span className="text-muted-foreground">- {g.signatoryEmail}</span>
                                </div>
                              );
                            }
                            return null;
                          })}
                          
                          {/* SimplyFund Lender Signer */}
                          {sfSigner.name && sfSigner.email && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                              <Building className="w-3 h-3 text-blue-600" />
                              <span className="text-blue-700 font-medium">{sfSigner.name} (Lender)</span>
                              <span className="text-muted-foreground">- {sfSigner.email}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Document Selection */}
                      <div className="text-left bg-blue-50 rounded-lg p-4 mb-4">
                        <p className="text-sm font-medium mb-3">Documents to Include:</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              id="ftla-doc" 
                              checked={eSigningDocs.ftla} 
                              disabled={true}
                              className="data-[state=checked]:bg-blue-600"
                            />
                            <label htmlFor="ftla-doc" className="text-sm">
                              Fixed Term Loan Agreement (FTLA) <span className="text-muted-foreground">- Required</span>
                            </label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              id="gsd-doc" 
                              checked={eSigningDocs.gsd}
                              onCheckedChange={(checked) => setESigningDocs(prev => ({...prev, gsd: checked}))}
                              className="data-[state=checked]:bg-blue-600"
                            />
                            <label htmlFor="gsd-doc" className="text-sm cursor-pointer">
                              General Security Deed (GSD) <span className="text-muted-foreground">- Optional</span>
                            </label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="bpd-doc"
                              checked={bpdIncluded}
                              disabled={bpdRequired}
                              onCheckedChange={(checked) => setESigningDocs(prev => ({...prev, bpd: !!checked}))}
                              className="data-[state=checked]:bg-blue-600"
                              data-testid="aus-bpd-checkbox"
                            />
                            <label htmlFor="bpd-doc" className="text-sm cursor-pointer" data-testid="aus-bpd-label">
                              Business Purpose Declaration (NCC s13){" "}
                              {bpdRequired
                                ? <span className="text-amber-700 font-medium">- Required for sole traders</span>
                                : <span className="text-muted-foreground">- Optional (partnerships / individual trustees)</span>}
                            </label>
                          </div>
                        </div>
                        {bpdRequired && (
                          <p className="text-xs text-amber-700 mt-2" data-testid="aus-bpd-required-note">
                            {companyDetails?.source === "ASIC" || companyDetails?.source === "manual" ? "This borrower is recorded" : "The ABR lists this borrower"} as “{companyDetails?.company_type || "Individual/Sole Trader"}”. Without a signed Business Purpose Declaration the loan could fall under the National Credit Code — it is placed first in the envelope so it is signed before the FTLA.
                          </p>
                        )}
                        {!bpdRequired && bpdIncluded && (
                          <p className="text-xs text-blue-600 mt-2" data-testid="aus-bpd-optional-note">
                            Business Purpose Declaration will be generated and placed first in the DocuSign envelope
                          </p>
                        )}
                        {eSigningDocs.gsd && (
                          <p className="text-xs text-blue-600 mt-2">
                            GSD will be generated and included in the DocuSign envelope
                          </p>
                        )}
                      </div>
                      
                      <Button 
                        onClick={sendToDocuSign}
                        disabled={sendingToDocuSign || (!signingDirector1)}
                        className="w-full"
                        size="lg"
                      >
                        {sendingToDocuSign ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating Envelope...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send to DocuSign
                          </>
                        )}
                      </Button>
                      
                      {docuSignResult && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-left">
                          <p className="text-sm font-medium text-green-800">
                            ✓ Envelope created: {docuSignResult.envelope_id}
                          </p>
                          {docuSignResult.business_purpose_declaration?.included && (
                            <p className="text-xs text-green-700 mt-1" data-testid="aus-bpd-envelope-note">
                              Business Purpose Declaration (NCC s13) is document 1 — signed before the FTLA
                              {docuSignResult.business_purpose_declaration?.entity?.abr_checked ? " · entity type re-verified with the ABR" : ""}
                            </p>
                          )}
                          {docuSignResult.is_mock && (
                            <p className="text-xs text-amber-600 mt-1">
                              Note: DocuSign is in mock mode. Configure credentials in Settings.
                            </p>
                          )}
                        </div>
                      )}

                      {/* SMS the signing link to customer */}
                      {docuSignResult?.envelope_id && (
                        <DocuSignSmsPanel
                          envelope={docuSignResult}
                          signers={signers.filter((s) => s.email)}
                          defaultMobile={
                            (typeof signingDirector1 === "object"
                              ? (signingDirector1?.phone || signingDirector1?.mobile)
                              : "") || ""
                          }
                          aircallNumbers={gcAircallNumbers}
                          aircallPhoneId={gcAircallPhoneId}
                          loadAircallNumbers={loadAircallNumbers}
                          loadingNumbers={gcLoadingNumbers}
                          defaultName={
                            typeof signingDirector1 === "object"
                              ? (signingDirector1?.name || signingDirector1?.full_name || "")
                              : (signingDirector1 || "")
                          }
                          businessName={companyDetails?.company_name || selectedCompany?.company_name || ""}
                          sfaNumber={loanDetails?.fundingAgreementNumber || ""}
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Fallback: DocuPilot option */}
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-2">
                    Or use DocuPilot for e-signing:
                  </p>
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      window.open(`https://${DOCUPILOT_SUBDOMAIN}.docupilot.app/~/dashboard/workspace/${DOCUPILOT_WORKSPACE_ID}/envelopes`, '_blank');
                    }}
                  >
                    <FileSignature className="w-3 h-3 mr-1" />
                    Open DocuPilot
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Show message that DocuSign is open in modal */}
                <Card className="mb-4">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileSignature className="w-8 h-8 text-blue-600 animate-pulse" />
                    </div>
                    <h4 className="font-semibold mb-2">DocuSign Editor Open</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      The DocuSign tagging interface is open in a larger window. 
                      Place signature fields on the document and click Send when ready.
                    </p>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowDocuSignIframe(false);
                        setDocuSignIframeUrl(null);
                      }}
                    >
                      Close DocuSign
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </>
      ) : generating ? (
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Generating Document...</h3>
          <p className="text-muted-foreground">
            Please wait while we generate your loan agreement.
          </p>
        </div>
      ) : (
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Generation Failed</h3>
          <p className="text-muted-foreground">
            There was an error generating your document. Please try again.
          </p>
        </div>
      )}
    </div>
  );

  // GoCardless: Create billing request (mandate)
  const createGcMandate = async () => {
    setGcCreating(true);
    try {
      const customerName = typeof signingDirector1 === 'object' 
        ? (signingDirector1?.name || signingDirector1?.full_name || "") 
        : (signingDirector1 || "");
      const businessName = companyDetails?.company_name || selectedCompany?.company_name || "";
      const sfaNum = loanDetails.fundingAgreementNumber || "";

      // Split customer name into given/family for GC prefill
      const nameParts = customerName.trim().split(/\s+/);
      const givenName = nameParts[0] || "";
      const familyName = nameParts.slice(1).join(" ") || "";

      // Get customer email from director or guarantor
      const customerEmail = typeof signingDirector1 === 'object' 
        ? (signingDirector1?.email || "") 
        : "";

      const resp = await api.post("/aus/gocardless/billing-request", {
        scheme: gcMandate.scheme,
        customer_name: customerName,
        business_name: businessName,
        sfa_number: sfaNum,
        description: `${sfaNum} - ${businessName} - ${gcMandate.scheme === "au_pay_to" ? "PayTo" : "Direct Debit"}`,
        customer_email: customerEmail,
        customer_given_name: givenName,
        customer_family_name: familyName,
        // Pre-fill bank details from Step 3 — customer can still edit them in the GoCardless flow
        bank_account_holder_name: borrowerBankDetails?.accountName || "",
        bank_branch_code: borrowerBankDetails?.bsb || "",
        bank_account_number: borrowerBankDetails?.accountNumber || "",
        // PayTo-only: GoCardless requires a per-payment cap + frequency.
        // Backend converts dollars → cents and adds purpose_code='loan'.
        payment_amount: gcMandate.amount ? parseFloat(gcMandate.amount) : null,
        payment_frequency: gcMandate.frequency || null,
      });

      setGcResult(resp.data);
      // Pre-fill email field with customer email if available
      if (customerEmail && !gcEmailTo) {
        setGcEmailTo(customerEmail);
      }
      // Pre-fill SMS mobile from director if available
      const directorPhone = typeof signingDirector1 === 'object'
        ? (signingDirector1?.phone || signingDirector1?.mobile || "")
        : "";
      if (directorPhone && !gcSmsTo) {
        setGcSmsTo(directorPhone);
      }
      // Lazily fetch Aircall numbers when mandate is created
      loadAircallNumbers();
      // Load + pre-fill the SMS template (placeholders pre-substituted, {shortUrl} kept)
      prepareSmsBody(customerName, businessName, sfaNum);
      toast.success(`${gcMandate.scheme === "au_pay_to" ? "PayTo" : "Direct Debit"} mandate created`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to create mandate";
      toast.error(msg);
    } finally {
      setGcCreating(false);
    }
  };

  // GoCardless: Switch an existing PayTo mandate to BECS when the customer's
  // bank doesn't support PayTo. Re-creates the billing request with the SAME
  // pre-filled data (customer, bank account, SFA, amount, frequency) but with
  // scheme="becs". The original PayTo billing request stays in the DB for
  // audit but is superseded by the new BECS one in `gcResult`.
  const switchToBecsFallback = async () => {
    if (!gcResult?.success) return;
    setGcCreating(true);
    try {
      const customerName = typeof signingDirector1 === 'object' 
        ? (signingDirector1?.name || signingDirector1?.full_name || "") 
        : (signingDirector1 || "");
      const businessName = companyDetails?.company_name || selectedCompany?.company_name || "";
      const sfaNum = loanDetails.fundingAgreementNumber || "";
      const nameParts = customerName.trim().split(/\s+/);
      const givenName = nameParts[0] || "";
      const familyName = nameParts.slice(1).join(" ") || "";
      const customerEmail = typeof signingDirector1 === 'object' 
        ? (signingDirector1?.email || "") 
        : "";

      const resp = await api.post("/aus/gocardless/billing-request", {
        scheme: "becs",
        customer_name: customerName,
        business_name: businessName,
        sfa_number: sfaNum,
        description: `${sfaNum} - ${businessName} - BECS (PayTo fallback)`,
        customer_email: customerEmail,
        customer_given_name: givenName,
        customer_family_name: familyName,
        bank_account_holder_name: borrowerBankDetails?.accountName || "",
        bank_branch_code: borrowerBankDetails?.bsb || "",
        bank_account_number: borrowerBankDetails?.accountNumber || "",
        // BECS doesn't use these but pass them anyway in case of future routing
        payment_amount: gcMandate.amount ? parseFloat(gcMandate.amount) : null,
        payment_frequency: gcMandate.frequency || null,
      });

      // Switch the UI scheme so the rest of the panel reflects BECS
      setGcMandate(prev => ({ ...prev, scheme: "becs" }));
      setGcResult(resp.data);
      // Rebuild the SMS body for the new scheme label
      prepareSmsBody(customerName, businessName, sfaNum);
      toast.success("Switched to BECS Direct Debit — new authorisation link ready");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to switch to BECS";
      toast.error(msg);
    } finally {
      setGcCreating(false);
    }
  };

  // Build the SMS body for editing: fetch the master template and substitute
  // every placeholder except {shortUrl} (which doesn't exist until send-time).
  const prepareSmsBody = async (customerName, businessName, sfaNumber) => {
    try {
      const resp = await api.get("/aus/sms-template");
      const tmpl = resp.data?.template || "";
      setGcSmsTemplate(tmpl);
      const firstName = (customerName || "").trim().split(/\s+/)[0] || "there";
      const schemeLabel = gcMandate.scheme === "au_pay_to" ? "PayTo" : "Direct Debit";
      const rendered = tmpl
        .replace(/\{firstName\}/g, firstName)
        .replace(/\{customerName\}/g, customerName || "")
        .replace(/\{businessName\}/g, businessName || "")
        .replace(/\{scheme\}/g, schemeLabel)
        .replace(/\{sfaNumber\}/g, sfaNumber || "");
      // {shortUrl} stays so the operator can see where the link will go
      setGcSmsBody(rendered);
    } catch (e) {
      console.error("Failed to load SMS template:", e);
    }
  };

  // Fetch Aircall numbers (for SMS dropdown). Cached after first load.
  // Returns ONLY API-messaging-enabled AU numbers (dedicated portal lines).
  // Personal team lines (Dave/Nick/Dean) are intentionally hidden so they
  // can't be accidentally configured for Public API messaging (which would
  // disable manual SMS in the Aircall app for those users).
  const loadAircallNumbers = async () => {
    if (gcAircallNumbers.length > 0 || gcLoadingNumbers) return;
    setGcLoadingNumbers(true);
    try {
      const resp = await api.get("/aus/aircall/numbers");
      const all = resp.data?.numbers || [];
      // AU-only filter — by country code OR digits starting with +61
      const auOnly = all.filter(
        (n) =>
          n.country === "AU" ||
          /^\+?61\s*/.test((n.digits || "").replace(/\s/g, ""))
      );
      // Heuristic: prefer dedicated API/portal numbers (name contains "API",
      // "Portal", or "SMS"). Falls back to all AU mobiles if none match.
      const dedicated = auOnly.filter((n) =>
        /api|portal|sms/i.test(n.name || "")
      );
      const candidates = dedicated.length > 0 ? dedicated : auOnly;
      setGcAircallNumbers(candidates);
      const auto = candidates[0];
      if (auto && !gcAircallPhoneId) setGcAircallPhoneId(String(auto.id));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "";
      if (msg.includes("credentials missing")) {
        // Quiet — user just hasn't configured Aircall yet
      } else {
        console.error("Aircall load error:", err);
      }
    } finally {
      setGcLoadingNumbers(false);
    }
  };

  // Send the authorisation link via Aircall SMS
  const sendGcSms = async () => {
    if (!gcResult?.authorisation_url) return;
    if (!gcSmsTo) {
      toast.error("Enter the customer's mobile number");
      return;
    }
    if (!gcAircallPhoneId) {
      toast.error("Select an Aircall number to send from");
      return;
    }
    setGcSendingSms(true);
    setGcSmsResult(null);
    setGcSmsStatus(null);
    try {
      const customerName = typeof signingDirector1 === 'object'
        ? (signingDirector1?.name || signingDirector1?.full_name || "")
        : (signingDirector1 || "");
      const businessName = companyDetails?.company_name || selectedCompany?.company_name || "";
      const sfaNum = loanDetails.fundingAgreementNumber || "";

      const resp = await api.post("/aus/gocardless/send-mandate-sms", {
        customer_mobile: gcSmsTo,
        customer_name: customerName,
        business_name: businessName,
        authorisation_url: gcResult.authorisation_url,
        scheme: gcResult.scheme || gcMandate.scheme,
        sfa_number: sfaNum,
        aircall_phone_id: gcAircallPhoneId,
        billing_request_id: gcResult.billing_request_id,
        custom_body: gcSmsBody || undefined,  // operator-edited body (with {shortUrl} placeholder)
      });
      setGcSmsResult({ success: true, ...resp.data });
      setGcSmsStatus({ resolved_status: "sent", received_at: new Date().toISOString() });
      toast.success(`SMS sent to ${resp.data.to}`);
      // Start polling for delivery status
      pollSmsStatus(resp.data.to);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to send SMS";
      setGcSmsResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setGcSendingSms(false);
    }
  };

  // Poll Aircall webhook events for ~30s to surface delivery status
  const pollSmsStatus = (toNumber) => {
    let attempts = 0;
    const maxAttempts = 10; // ~30s @ 3s interval
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const resp = await api.get("/aus/aircall/sms-status", {
          params: { to: toNumber, since_seconds: 600 },
        });
        if (resp.data?.latest) {
          setGcSmsStatus(resp.data.latest);
          const s = resp.data.latest.resolved_status;
          if (s === "delivered" || s === "failed" || s === "undelivered") {
            clearInterval(interval);
          }
        }
      } catch (e) {
        // ignore polling errors
      }
      if (attempts >= maxAttempts) clearInterval(interval);
    }, 3000);
  };

  // Step 6: GoCardless Mandate Setup
  const renderStep6GoCardless = () => {
    const businessName = companyDetails?.company_name || selectedCompany?.company_name || "";
    const sfaNum = loanDetails.fundingAgreementNumber || "";

    // Auto-fill product name if empty
    if (!gcMandate.productName && sfaNum) {
      const prefix = sfaNum.toUpperCase().startsWith("SFA-") ? "" : "SFA-";
      setGcMandate(prev => ({ ...prev, productName: `${prefix}${sfaNum} - ${businessName}` }));
    }

    // Default first payment date to 3 business days from now
    if (!gcMandate.firstPaymentDate) {
      const d = new Date();
      d.setDate(d.getDate() + 3);
      // Skip weekends
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
      const localYmd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      setGcMandate(prev => ({ ...prev, firstPaymentDate: localYmd }));
    }

    // Default amount from calculated payment amount.
    // For PayTo, when `autoCapFromLoan` is on, keep `amount` synced to the
    // loan's per-payment amount on every render so the cap can't drift.
    if (calculatedFields.paymentAmount) {
      const loanAmt = String(calculatedFields.paymentAmount.toFixed(2));
      const isPayto = gcMandate.scheme === "au_pay_to";
      const shouldLock = isPayto && gcMandate.autoCapFromLoan;
      if (!gcMandate.amount) {
        setGcMandate(prev => ({ ...prev, amount: loanAmt }));
      } else if (shouldLock && gcMandate.amount !== loanAmt) {
        setGcMandate(prev => ({ ...prev, amount: loanAmt }));
      }
    }

    // Default frequency from loan details
    if (gcMandate.frequency === "weekly" && loanDetails.paymentFrequency) {
      const freqMap = { "Daily": "daily", "Weekly": "weekly", "Monthly": "monthly" };
      const mapped = freqMap[loanDetails.paymentFrequency] || "weekly";
      if (mapped !== gcMandate.frequency) {
        setGcMandate(prev => ({ ...prev, frequency: mapped }));
      }
      // Policy: daily plans collect by BECS Direct Debit; PayTo stays the
      // standard for weekly. One-shot with the frequency default so an
      // operator override afterwards is respected.
      if (mapped === "daily" && gcMandate.scheme === "au_pay_to" && !gcResult) {
        setGcMandate(prev => ({ ...prev, scheme: "becs" }));
      }
    }

    // Default payment reference
    if (!gcMandate.paymentReference && sfaNum) {
      const prefix = sfaNum.toUpperCase().startsWith("SFA-") ? "" : "SFA-";
      setGcMandate(prev => ({ ...prev, paymentReference: `${prefix}${sfaNum}` }));
    }

    return (
      <div className="space-y-5">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-1">
            <Building className="w-5 h-5 text-emerald-700" />
            <h3 className="font-semibold text-emerald-900">GoCardless Mandate</h3>
          </div>
          <p className="text-sm text-emerald-700 ml-8">
            Create a PayTo or Direct Debit mandate for <strong>{businessName}</strong>. 
            Policy: PayTo is the standard for weekly plans (covers ~80% of AU banks); daily plans and PayTo-refusing
            banks collect by BECS Direct Debit.
          </p>
        </div>

        {/* Scheme Selection — PayTo is the default (covers ~80% of AU banks). 
            BECS Direct Debit stays as the fallback for banks that don't support PayTo. */}
        <div className="space-y-2">
          <Label className="font-medium">Payment Type</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className={cn(
                "p-4 rounded-lg border-2 text-left transition-all",
                gcMandate.scheme === "au_pay_to" 
                  ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" 
                  : "border-gray-200 hover:border-gray-300"
              )}
              onClick={() => setGcMandate(prev => ({ ...prev, scheme: "au_pay_to" }))}
              data-testid="gc-scheme-payto"
            >
              <div className="font-medium text-sm flex items-center gap-2">
                PayTo
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 border-emerald-300">
                  Recommended · ~80% of AU banks
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Real-time payments with instant failure notifications. Customer can authorise via their banking app.
              </p>
            </button>
            <button
              type="button"
              className={cn(
                "p-4 rounded-lg border-2 text-left transition-all",
                gcMandate.scheme === "becs" 
                  ? "border-blue-500 bg-blue-50" 
                  : "border-gray-200 hover:border-gray-300"
              )}
              onClick={() => setGcMandate(prev => ({ ...prev, scheme: "becs" }))}
              data-testid="gc-scheme-becs"
            >
              <div className="font-medium text-sm flex items-center gap-2">
                Direct Debit (BECS)
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-100 text-slate-700 border-slate-300">
                  Fallback
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Standard bank debit. Use when the customer's bank doesn't support PayTo.
              </p>
            </button>
          </div>
        </div>

        {gcMandate.scheme === "au_pay_to" && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              <strong>PayTo Note:</strong> The first payment will be deferred to the start date you set when creating the subscription. 
              This ensures the customer is not charged before receiving their funds.
            </p>
          </div>
        )}

        {/* PayTo cap auto-sync toggle — keeps GoCardless `max_amount_per_payment`
            locked to the loan's calculated per-payment amount so the mandate
            scope matches the loan schedule exactly. Operator can untick to
            override (e.g. to allow a small buffer for arrears). */}
        {gcMandate.scheme === "au_pay_to" && calculatedFields.paymentAmount > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-3" data-testid="payto-auto-cap-block">
            <Checkbox
              id="gc-auto-cap"
              checked={gcMandate.autoCapFromLoan}
              onCheckedChange={(v) => setGcMandate(prev => ({ ...prev, autoCapFromLoan: !!v }))}
              data-testid="payto-auto-cap-toggle"
              className="mt-0.5"
            />
            <div className="flex-1 text-xs text-emerald-900">
              <label htmlFor="gc-auto-cap" className="font-semibold cursor-pointer">
                Use the loan's payment amount as the PayTo cap
              </label>
              <p className="text-emerald-800/80 mt-0.5">
                Locks the GoCardless <code className="font-mono">max_amount_per_payment</code> to{" "}
                <strong>${calculatedFields.paymentAmount.toFixed(2)} AUD</strong>
                {loanDetails.paymentFrequency ? ` (${loanDetails.paymentFrequency.toLowerCase()})` : ""}
                {" "}— the customer can't be charged more than one scheduled instalment per payment. Untick to override.
              </p>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Product / Subscription Name</Label>
            <Input
              value={gcMandate.productName}
              onChange={(e) => setGcMandate(prev => ({ ...prev, productName: e.target.value }))}
              placeholder="e.g. SFA-1039 - Acme Pty Ltd"
              data-testid="gc-product-name"
            />
          </div>

          <div>
            <Label>
              Payment Amount ($AUD)
              {gcMandate.scheme === "au_pay_to" && gcMandate.autoCapFromLoan && (
                <span className="ml-2 text-[10px] text-emerald-700 font-normal">· locked to loan</span>
              )}
            </Label>
            <Input
              type="number"
              step="0.01"
              value={gcMandate.amount}
              onChange={(e) => setGcMandate(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="e.g. 61.50"
              data-testid="gc-amount"
              readOnly={gcMandate.scheme === "au_pay_to" && gcMandate.autoCapFromLoan}
              className={gcMandate.scheme === "au_pay_to" && gcMandate.autoCapFromLoan ? "bg-emerald-50 cursor-not-allowed" : ""}
            />
          </div>

          <div>
            <Label>Payment Frequency</Label>
            <Select 
              value={gcMandate.frequency} 
              onValueChange={(v) => setGcMandate(prev => ({ ...prev, frequency: v }))}
            >
              <SelectTrigger data-testid="gc-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Payment Reference</Label>
            <Input
              value={gcMandate.paymentReference}
              onChange={(e) => setGcMandate(prev => ({ ...prev, paymentReference: e.target.value }))}
              placeholder="e.g. SFA-1039"
              data-testid="gc-payment-ref"
            />
          </div>

          <div>
            <Label>First Payment Date</Label>
            <Input
              type="date"
              value={gcMandate.firstPaymentDate}
              onChange={(e) => setGcMandate(prev => ({ ...prev, firstPaymentDate: e.target.value }))}
              data-testid="gc-first-payment-date"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Earliest: 2-3 business days after funding
            </p>
          </div>
        </div>

        {/* Summary before submission */}
        <div className="bg-slate-50 border rounded-lg p-4 space-y-2">
          <h4 className="text-sm font-semibold text-slate-800">Mandate Summary</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <span className="text-muted-foreground">Business:</span>
            <span className="font-medium">{businessName}</span>
            <span className="text-muted-foreground">Type:</span>
            <span className="font-medium">{gcMandate.scheme === "au_pay_to" ? "PayTo" : "Direct Debit (BECS)"}</span>
            <span className="text-muted-foreground">Amount:</span>
            <span className="font-medium">${gcMandate.amount || "—"} AUD</span>
            <span className="text-muted-foreground">Frequency:</span>
            <span className="font-medium capitalize">{gcMandate.frequency}</span>
            <span className="text-muted-foreground">First Payment:</span>
            <span className="font-medium">{gcMandate.firstPaymentDate || "—"}</span>
            <span className="text-muted-foreground">Reference:</span>
            <span className="font-medium">{gcMandate.paymentReference || "—"}</span>
          </div>
        </div>

        {/* Create Button */}
        {!gcResult && (
          <Button
            onClick={createGcMandate}
            disabled={gcCreating}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="gc-create-mandate-btn"
          >
            {gcCreating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Mandate...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Create {gcMandate.scheme === "au_pay_to" ? "PayTo" : "Direct Debit"} Mandate
              </>
            )}
          </Button>
        )}

        {/* Result - Auth URL */}
        {gcResult?.success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-green-800">Mandate Created Successfully</h4>
            </div>
            <div className="text-sm space-y-1 text-green-800">
              <p><strong>Billing Request:</strong> {gcResult.billing_request_id}</p>
              <p><strong>Scheme:</strong> {gcResult.scheme === "au_pay_to" ? "PayTo" : "Direct Debit (BECS)"}</p>
              <p><strong>Status:</strong> <Badge variant="outline" className="bg-green-100">{gcResult.status}</Badge></p>
            </div>

            {/* PayTo → BECS fallback. Some AU banks (~20%) still don't support
                PayTo. If the customer reports they can't authorise via PayTo,
                this re-issues a fresh BECS mandate with the same prefilled data. */}
            {gcResult.scheme === "au_pay_to" && (
              <div className="bg-white/70 border border-amber-200 rounded-md p-3 flex items-start gap-3" data-testid="payto-fallback-block">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 text-xs">
                  <p className="text-amber-900 font-medium">Customer's bank doesn't support PayTo?</p>
                  <p className="text-amber-800/90 mt-0.5">
                    PayTo covers ~80% of AU banks. If they can't authorise, we'll re-issue this mandate as a BECS Direct Debit with the same pre-filled details — no need to redo Steps 1-5.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={switchToBecsFallback}
                  disabled={gcCreating}
                  className="bg-white border-amber-300 text-amber-900 hover:bg-amber-50 shrink-0"
                  data-testid="gc-switch-to-becs-btn"
                >
                  {gcCreating ? (
                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  ) : null}
                  Switch to BECS
                </Button>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-green-800 font-medium">Customer Authorisation Link</Label>
              <div className="flex gap-2">
                <Input 
                  readOnly 
                  value={gcResult.authorisation_url || ""} 
                  className="text-xs bg-white"
                  data-testid="gc-auth-url"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(gcResult.authorisation_url);
                    setGcLinkCopied(true);
                    toast.success("Link copied to clipboard");
                    setTimeout(() => setGcLinkCopied(false), 2000);
                  }}
                  data-testid="gc-copy-link-btn"
                >
                  {gcLinkCopied ? <CheckCircle className="w-4 h-4 text-green-600" /> : "Copy"}
                </Button>
              </div>
            </div>

            {/* Send link via Aircall SMS */}
            <Separator />
            <div className="space-y-2">
              <Label className="text-green-800 font-medium flex items-center gap-2">
                <Send className="w-4 h-4" />
                Send authorisation link via SMS
              </Label>
              <p className="text-xs text-green-700">
                A shortened version of the link will be sent via Aircall SMS to the customer's mobile.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-green-800">Customer mobile (AU)</Label>
                  <Input
                    placeholder="04xx xxx xxx"
                    value={gcSmsTo}
                    onChange={(e) => setGcSmsTo(e.target.value)}
                    className="bg-white"
                    data-testid="gc-sms-to-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-green-800">Sending from</Label>
                  <div
                    className="h-9 rounded-md border border-green-200 bg-white px-3 flex items-center text-sm text-slate-700"
                    data-testid="gc-aircall-phone-label"
                  >
                    {gcLoadingNumbers && <Loader2 className="w-3 h-3 mr-2 animate-spin text-slate-400" />}
                    {(() => {
                      const selected = gcAircallNumbers.find((n) => String(n.id) === String(gcAircallPhoneId));
                      if (gcLoadingNumbers) return "Loading portal number…";
                      if (!selected) return "No portal SMS line configured — check Aircall settings";
                      return (
                        <span className="truncate">
                          <span className="font-medium">{selected.digits}</span>
                          <span className="text-slate-500 text-xs ml-2">{selected.name}</span>
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Editable SMS body */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-green-800">SMS message</Label>
                  <span className="text-[10px] text-muted-foreground">
                    {gcSmsBody.length} chars{" "}
                    {gcSmsBody.length > 160 && (
                      <span className="text-amber-700">
                        ({Math.ceil(gcSmsBody.length / 160)} segments)
                      </span>
                    )}
                  </span>
                </div>
                <Textarea
                  value={gcSmsBody}
                  onChange={(e) => setGcSmsBody(e.target.value)}
                  rows={3}
                  className="bg-white text-sm font-mono resize-y"
                  data-testid="gc-sms-body-textarea"
                  placeholder="Loading template…"
                />
                <p className="text-[11px] text-muted-foreground">
                  <code className="bg-white px-1 py-0.5 rounded">{"{shortUrl}"}</code> is replaced with the actual short link on send.
                </p>
              </div>

              <Button
                size="sm"
                onClick={sendGcSms}
                disabled={gcSendingSms || !gcSmsTo || !gcAircallPhoneId || !gcSmsBody}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="gc-send-sms-btn"
              >
                {gcSendingSms ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending SMS…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send SMS</>
                )}
              </Button>

              {gcSmsResult?.success && (
                <div className="text-xs bg-white border border-green-200 rounded-md p-2 space-y-1" data-testid="gc-sms-success">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-green-700">✅ Sent to {gcSmsResult.to}</p>
                    {gcSmsStatus && (
                      <span
                        className={
                          "text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold " +
                          (gcSmsStatus.resolved_status === "delivered"
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                            : gcSmsStatus.resolved_status === "failed" || gcSmsStatus.resolved_status === "undelivered"
                              ? "bg-red-100 text-red-800 border border-red-300"
                              : "bg-amber-100 text-amber-800 border border-amber-300")
                        }
                        data-testid="gc-sms-status-badge"
                      >
                        {gcSmsStatus.resolved_status === "delivered" && "✓✓ Delivered"}
                        {gcSmsStatus.resolved_status === "sent" && "✓ Sent — waiting for delivery…"}
                        {gcSmsStatus.resolved_status === "queued" && "● Queued"}
                        {gcSmsStatus.resolved_status === "failed" && "✗ Failed"}
                        {gcSmsStatus.resolved_status === "undelivered" && "✗ Undelivered"}
                        {!["delivered","sent","queued","failed","undelivered"].includes(gcSmsStatus.resolved_status) && gcSmsStatus.resolved_status}
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground break-all">Short link: {gcSmsResult.short_url}</p>
                  <p className="text-muted-foreground">Preview: {gcSmsResult.sms_preview} ({gcSmsResult.length} chars)</p>
                </div>
              )}
              {gcSmsResult && !gcSmsResult.success && (
                <p className="text-xs text-red-600" data-testid="gc-sms-error">{gcSmsResult.error}</p>
              )}
            </div>

            {/* Recent SMS history */}
            <Separator />
            <RecentSmsTable
              sfaNumber={loanDetails?.fundingAgreementNumber}
              limit={5}
              onResend={(send) => {
                if (send?.to) setGcSmsTo(send.to);
                toast.info("Mobile number copied — review the message and click Send SMS to resend");
              }}
            />

            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => { setGcResult(null); setGcLinkCopied(false); }}
              data-testid="gc-create-another-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create Another Mandate
            </Button>
          </div>
        )}

        {gcResult && !gcResult.success && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <h4 className="font-semibold text-red-800">Failed to Create Mandate</h4>
            </div>
            <p className="text-sm text-red-700 mt-1">{gcResult.error || "Unknown error"}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setGcResult(null)}
            >
              Try Again
            </Button>
          </div>
        )}
      </div>
    );
  };


  // Navigation validation
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedCompany && loanDetails.fundingAgreementNumber;
      case 2:
        return loanDetails.loanAmount > 0 && loanDetails.commencementDate;
      case 3:
        return borrowerBankDetails.accountName && borrowerBankDetails.bsb && borrowerBankDetails.accountNumber && signingDirector1;
      case 4:
        // Check guarantor validation based on type
        const g = guarantors[0];
        if (!g) return false;
        if (g.guaranteeType === "personal") {
          return g.personalName && g.personalAddress;
        } else {
          return g.companyName && g.companyAddress && g.signatoryName;
        }
      default:
        return true;
    }
  };

  return (
    <div className={wizardOnly ? "" : "space-y-6"} data-testid="aus-document-generation">
      {!wizardOnly && (<>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Onboarding Centre</h1>
          <p className="text-muted-foreground">
            End-to-end customer onboarding — agreements, e-signature & direct debit mandates
          </p>
        </div>
        <Button onClick={startGeneration} data-testid="aus-new-document-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Onboarding
        </Button>
      </div>

      {/* Onboarding Status Dashboard */}
      <OnboardingStatusDashboard
        customer={signingDirector1}
        business={companyDetails?.company_name || selectedCompany?.company_name || ""}
        sfaNumber={loanDetails?.fundingAgreementNumber || ""}
        generatedDoc={generatedDoc}
        docuSignResult={docuSignResult}
        gcResult={gcResult}
        envelopes={envelopes || []}
        activeTile={activeTile}
        onTileClick={(id) => {
          // Toggle: clicking the active tile closes the panel
          setActiveTile((prev) => (prev === id ? null : id));
        }}
      />

      {/* Active tile drill-down panel */}
      {activeTile && (
        <TilePanel
          tile={activeTile}
          envelopes={envelopes || []}
          onOpenEnvelope={(env) => {
            // Reuse existing envelope-open behaviour
            if (env?.envelope_id && typeof loadDocuSignEnvelopes === "function") {
              // Just navigate; user can click "Open" in the envelopes section
              setActiveTile(null);
            }
          }}
          onResumeSession={(s) => {
            toast.info(`Resume not yet wired — SFA ${s.sfa_number} (${s.business_name})`);
            setActiveTile(null);
          }}
          onScrollToStep={(stepNum) => {
            // Open the wizard at the matching step
            setShowGenerateDialog(true);
            setCurrentStep(stepNum);
            setActiveTile(null);
          }}
        />
      )}

      {/* Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Onboarding Centre</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Generate Fixed Term Loan Agreements with Personal Guarantee, send for e-signature via DocuSign,
                and create BECS/PayTo direct debit mandates via GoCardless — all from one place.
                Company information is pulled from the ASIC database.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">ASIC Lookup</Badge>
                <Badge variant="secondary">DocuPilot Template 103526</Badge>
                <Badge variant="secondary">DocuSign e-Sign</Badge>
                <Badge variant="secondary">GoCardless BECS/PayTo</Badge>
                <Badge variant="secondary">Aircall SMS</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DocuSign Envelopes Section */}
      {/* DocuSign Envelopes Section - Collapsible */}
      <Card>
        <CardHeader 
          className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setEnvelopesExpanded(!envelopesExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {envelopesExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
              <FileSignature className="w-5 h-5 text-blue-600" />
              <div>
                <CardTitle className="text-lg">DocuSign Envelopes</CardTitle>
                <CardDescription>
                  Track document signing status
                  {!envelopesExpanded && envelopes.length > 0 && (
                    <span className="ml-2 text-blue-600">
                      ({envelopes.filter(e => e.status !== 'completed' && e.status !== 'voided' && e.status !== 'declined').length} open)
                    </span>
                  )}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {/* Filter Dropdown */}
              <Select value={envelopeFilter} onValueChange={setEnvelopeFilter}>
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <Filter className="w-3 h-3 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open Only</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="all">All Envelopes</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadDocuSignEnvelopes}
                disabled={loadingEnvelopes}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingEnvelopes ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        {envelopesExpanded && (
          <CardContent>
            {loadingEnvelopes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                <span className="text-muted-foreground">Loading envelopes...</span>
              </div>
            ) : envelopesError ? (
              <div className="text-center py-8">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-red-600">{envelopesError}</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={loadDocuSignEnvelopes}>
                  Try Again
                </Button>
              </div>
            ) : (() => {
              // Filter envelopes based on selection
              const filteredEnvelopes = envelopes.filter(env => {
                if (envelopeFilter === "open") {
                  return env.status !== 'completed' && env.status !== 'voided' && env.status !== 'declined';
                } else if (envelopeFilter === "completed") {
                  return env.status === 'completed';
                }
                return true; // "all"
              });
              
              if (filteredEnvelopes.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileSignature className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>
                      {envelopeFilter === "open" ? "No open envelopes" :
                       envelopeFilter === "completed" ? "No completed envelopes" :
                       "No envelopes found in the last 30 days"}
                    </p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredEnvelopes.map((env) => {
                    const isExpanded = expandedEnvelopes[env.envelope_id];
                    
                    // Quick status summary for collapsed view
                    const signedCount = env.signers.filter(s => s.signed_at).length;
                    const viewedCount = env.signers.filter(s => s.delivered_at && !s.signed_at).length;
                    const totalSigners = env.signers.length;
                    
                    return (
                      <div 
                        key={env.envelope_id} 
                        className="bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors overflow-hidden"
                      >
                        {/* Collapsed Header - Always Visible */}
                        <div 
                          className="p-3 cursor-pointer flex items-center gap-3 hover:bg-gray-100 transition-colors"
                          onClick={() => toggleEnvelopeExpand(env.envelope_id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900 truncate">{env.subject}</span>
                              <Badge 
                                className={`shrink-0 text-xs ${
                                  env.status === 'completed' ? 'bg-green-600' :
                                  env.status === 'delivered' ? 'bg-blue-600' :
                                  env.status === 'declined' || env.status === 'voided' ? 'bg-red-600' :
                                  env.status === 'sent' ? 'bg-amber-500' :
                                  'bg-gray-500'
                                }`}
                              >
                                {env.status_label}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              <span>
                                {env.sent_at ? new Date(env.sent_at).toLocaleDateString('en-AU', { 
                                  day: 'numeric', month: 'short'
                                }) : 'Not sent'}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span>
                                {signedCount > 0 && <span className="text-green-600">{signedCount} signed</span>}
                                {signedCount > 0 && viewedCount > 0 && ", "}
                                {viewedCount > 0 && <span className="text-blue-600">{viewedCount} viewed</span>}
                                {signedCount === 0 && viewedCount === 0 && <span>{totalSigners} pending</span>}
                                {signedCount > 0 && signedCount < totalSigners && `, ${totalSigners - signedCount - viewedCount} pending`}
                              </span>
                            </div>
                          </div>

                          {/* Resume button - only on Draft envelopes (status === 'created') */}
                          {env.status === 'created' && (
                            <Button
                              variant="default"
                              size="sm"
                              className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid={`resume-envelope-${env.envelope_id}`}
                              disabled={resumingEnvelopeId === env.envelope_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResumeEnvelope(env.envelope_id);
                              }}
                            >
                              {resumingEnvelopeId === env.envelope_id ? (
                                <>
                                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                  Opening…
                                </>
                              ) : (
                                <>
                                  <FileSignature className="w-3.5 h-3.5 mr-1" />
                                  Resume in DocuSign
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        
                        {/* Expanded Content - Signer Details */}
                        {isExpanded && (
                          <div className="px-3 pb-3 pt-0">
                            <div className="bg-white rounded-md border">
                              <div className="px-3 py-2 bg-gray-100 border-b text-xs font-medium text-gray-600 grid grid-cols-12 gap-2">
                                <span className="col-span-4">Signer</span>
                                <span className="col-span-4">Email</span>
                                <span className="col-span-2">Status</span>
                                <span className="col-span-2">Date</span>
                              </div>
                              {env.signers.map((signer, idx) => {
                                const signerStatus = signer.signed_at ? 'Signed' :
                                  signer.delivered_at ? 'Viewed' :
                                  signer.status === 'declined' ? 'Declined' :
                                  signer.status === 'sent' ? 'Sent' :
                                  'Pending';
                                
                                const statusDate = signer.signed_at || signer.delivered_at;
                                
                                return (
                                  <div 
                                    key={idx}
                                    className={`px-3 py-2 text-sm grid grid-cols-12 gap-2 items-center ${
                                      idx < env.signers.length - 1 ? 'border-b' : ''
                                    }`}
                                  >
                                    <span className="col-span-4 font-medium truncate" title={signer.name}>
                                      {signer.name}
                                    </span>
                                    <span className="col-span-4 text-muted-foreground truncate" title={signer.email}>
                                      {signer.email}
                                    </span>
                                    <span className={`col-span-2 inline-flex items-center gap-1 text-xs font-medium ${
                                      signerStatus === 'Signed' ? 'text-green-600' :
                                      signerStatus === 'Viewed' ? 'text-blue-600' :
                                      signerStatus === 'Declined' ? 'text-red-600' :
                                      signerStatus === 'Sent' ? 'text-amber-600' :
                                      'text-gray-500'
                                    }`}>
                                      {signerStatus === 'Signed' && <CheckCircle className="w-3 h-3" />}
                                      {signerStatus === 'Viewed' && <CheckCircle className="w-3 h-3" />}
                                      {signerStatus}
                                    </span>
                                    <span className="col-span-2 text-xs text-muted-foreground">
                                      {statusDate ? new Date(statusDate).toLocaleDateString('en-AU', {
                                        day: 'numeric', month: 'short'
                                      }) : '-'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Envelope ID footer */}
                            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                              <span className="font-mono">{env.envelope_id}</span>
                              {env.status === 'completed' && env.completed_at && (
                                <span className="text-green-600 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Completed {new Date(env.completed_at).toLocaleDateString('en-AU', {
                                    day: 'numeric', month: 'short', year: 'numeric'
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        )}
      </Card>
      </>)}

      {/* Generation Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={(open) => {
        if (!open && wizardOnly) {
          setShowDocuSignIframe(false);
          setDocuSignIframeUrl(null);
          setShowGenerateDialog(false);
          onWizardClose?.();
          return;
        }
        if (!open) {
          // Reset DocuSign iframe state when closing generation dialog
          setShowDocuSignIframe(false);
          setDocuSignIframeUrl(null);
        }
        setShowGenerateDialog(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">Customer Onboarding <WizardDebugButton className="ml-auto mr-6" /></DialogTitle>
            <DialogDescription>
              Step {currentStep} of 6 - {
                currentStep === 1 ? "Company Selection" :
                currentStep === 2 ? "Loan Details" :
                currentStep === 3 ? "Bank Details & Signatories" :
                currentStep === 4 ? "Guarantors" :
                currentStep === 5 ? "Documents & E-Signing" :
                "GoCardless Mandate"
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-2 mb-4">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div 
                key={step}
                className={cn(
                  "flex-1 h-2 rounded-full",
                  step <= currentStep ? "bg-blue-500" : "bg-gray-200"
                )}
              />
            ))}
          </div>
          
          {renderStepContent()}
          
          <DialogFooter className="mt-6">
            {currentStep > 1 && currentStep <= 4 && (
              <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            
            {currentStep < 4 && (
              <Button 
                onClick={() => {
                  // Auto-fill commencement date when moving from step 1 to step 2
                  if (currentStep === 1 && !loanDetails.commencementDate && loanDetails.agreementDate) {
                    setLoanDetails(prev => ({
                      ...prev,
                      commencementDate: prev.agreementDate
                    }));
                  }
                  setCurrentStep(currentStep + 1);
                }}
                disabled={!canProceed()}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            
            {currentStep === 4 && (
              <Button 
                onClick={generateDocument}
                disabled={!canProceed() || generating}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Document
                  </>
                )}
              </Button>
            )}
            
            {currentStep === 5 && (
              <div className="flex gap-2 w-full justify-between">
                <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
                  Close
                </Button>
                <Button 
                  onClick={() => setCurrentStep(6)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  GoCardless Mandate
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {currentStep === 6 && (
              <div className="flex gap-2 w-full justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(5)}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back to Documents
                </Button>
                <Button onClick={() => setShowGenerateDialog(false)}>
                  Close
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* DocuSign Iframe Dialog - Large viewport modal like UK */}
      <Dialog open={showDocuSignIframe} onOpenChange={(open) => {
        if (!open) {
          setShowDocuSignIframe(false);
          setDocuSignIframeUrl(null);
          loadDocuSignEnvelopes(); // Refresh envelope list when closing
        }
      }}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] p-0">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-blue-50">
              <div className="flex items-center gap-3">
                <FileSignature className="w-6 h-6 text-blue-600" />
                <div>
                  <DialogTitle className="text-blue-900">DocuSign - Place Signature Fields</DialogTitle>
                  <DialogDescription className="text-blue-700">
                    Drag and drop signature fields onto the documents, then click Send in DocuSign
                  </DialogDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <WizardDebugButton />
                {docuSignResult?.envelope_id && (
                  <Badge variant="outline" className="bg-white text-xs">{docuSignResult.envelope_id}</Badge>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowDocuSignIframe(false);
                    setDocuSignIframeUrl(null);
                    loadDocuSignEnvelopes(); // Refresh envelope list
                  }}
                >
                  <X className="w-4 h-4 mr-1" />
                  Close & Return
                </Button>
              </div>
            </div>
            
            {/* DocuSign iframe */}
            <div className="flex-1 bg-gray-100">
              <iframe
                src={docuSignIframeUrl}
                className="w-full h-full border-0"
                title="DocuSign Sender View"
                allow="camera; microphone; clipboard-write"
              />
            </div>
            
            {/* Footer with actions */}
            <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Envelope ID: {docuSignResult?.envelope_id} • 
                {docuSignResult?.document_count || 1} document(s) • 
                {docuSignResult?.signer_count || 1} signer(s)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowDocuSignIframe(false);
                    setDocuSignIframeUrl(null);
                    toast.info("Progress saved - you can resume later");
                    loadDocuSignEnvelopes(); // Refresh envelope list
                  }}
                >
                  Save & Exit
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    setShowDocuSignIframe(false);
                    setDocuSignIframeUrl(null);
                    toast.success("Document sent for signing!");
                    loadDocuSignEnvelopes(); // Refresh envelope list
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  I've Sent in DocuSign
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
